import React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import { jsPDF } from "jspdf";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.mjs?url";
import { TAKEOFF_DB } from "../lib/takeoffDb.js";
import { drawingsApi, fetchAndCacheDrawing } from "../api/drawings.js";
import { r2 } from "../lib/constants.js";
import { GroupBlock } from "./GroupBlock.jsx";
import { TemplateItem } from "./TemplateItem.jsx";
import { TakeoffPdfPage } from "./TakeoffPdfPage.jsx";
import { TakeoffTemplateEditor } from "./TakeoffTemplateEditor.jsx";
import { KonfiguratorModal } from "./KonfiguratorModal.jsx";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export function TakeoffView({ activeProject, setProjects, projects, activeProjectId, ziniarastis, setAtliktiDarbai, atliktiDarbai,
          externalSelectedDrawingId, onSelectDrawing, onDeleteDrawing, onUploadDrawing, externalFileInputRef }) {
          const { useState, useRef, useEffect, useMemo, useCallback } = React;

          // ─── Šablonai (bendri visiems brėžiniams projekte) ───
          const templates = useMemo(() => activeProject ? (activeProject.takeoffTemplates || []) : [], [activeProject]);
          function setTemplates(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffTemplates || [];
              const v = typeof val === 'function' ? val(cur) : val;
              return { ...p, takeoffTemplates: v };
            }));
          }

          // ─── Šablonų grupės (saugomos atskirai nuo šablonų) ───
          const templateGroups = useMemo(() => {
            if (!activeProject) return [];
            const saved = activeProject.takeoffTemplateGroups || [];
            // Taip pat surenkame grupes iš šablonų groupName (atgalinis suderinamumas)
            const fromTemplates = (activeProject.takeoffTemplates || [])
              .map(t => (t.groupName || '').trim())
              .filter(g => g && !saved.includes(g));
            const merged = [...saved, ...fromTemplates];
            // Jei radome naujų grupių iš šablonų — išsaugome į projektą
            if (fromTemplates.length > 0) {
              setTimeout(() => {
                setProjects(prev => prev.map(p => {
                  if (p.id !== activeProjectId) return p;
                  const cur = p.takeoffTemplateGroups || [];
                  const toAdd = (p.takeoffTemplates || [])
                    .map(t => (t.groupName || '').trim())
                    .filter(g => g && !cur.includes(g));
                  if (toAdd.length === 0) return p;
                  return { ...p, takeoffTemplateGroups: [...cur, ...toAdd] };
                }));
              }, 0);
            }
            return merged;
          }, [activeProject]);
          function setTemplateGroups(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffTemplateGroups || [];
              const v = typeof val === 'function' ? val(cur) : val;
              return { ...p, takeoffTemplateGroups: v };
            }));
          }
          // Pridėti grupę jei dar nėra
          function ensureGroup(name) {
            const n = (name || '').trim();
            if (!n) return;
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffTemplateGroups || [];
              if (cur.includes(n)) return p;
              return { ...p, takeoffTemplateGroups: [...cur, n] };
            }));
          }

          // ─── Brėžiniai (PDF) ───
          const drawings = useMemo(() => activeProject ? (activeProject.takeoffDrawings || []) : [], [activeProject]);
          function setDrawings(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffDrawings || [];
              const v = typeof val === 'function' ? val(cur) : val;
              const stripped = v.map(({ _pdfBuf, ...rest }) => rest);
              return { ...p, takeoffDrawings: stripped };
            }));
          }

          // ─── Žymėjimai (sužymėti elementai, bendri per brėžinius) ───
          const markings = useMemo(() => activeProject ? (activeProject.takeoffMarkings || []) : [], [activeProject]);
          function setMarkings(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffMarkings || [];
              const v = typeof val === 'function' ? val(cur) : val;
              return { ...p, takeoffMarkings: v };
            }));
          }

          // ─── Sluoksniai (sistemos) — saugomi projekte ───
          const SYSTEM_PRESETS = [
            { key: 'kanalizacija', label: 'Kanalizacija',   color: '#8B4513' },
            { key: 'vandentiekis', label: 'Vandentiekis',   color: '#2563eb' },
            { key: 'sildymas',     label: 'Šildymas',       color: '#dc2626' },
            { key: 'vedinimas',    label: 'Vėdinimas',      color: '#059669' },
            { key: 'kondicionavimas', label: 'Kondicionavimas', color: '#7c3aed' },
          ];
          const layers = useMemo(() => activeProject ? (activeProject.takeoffLayers || []) : [], [activeProject]);
          function setLayers(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffLayers || [];
              const v = typeof val === 'function' ? val(cur) : val;
              return { ...p, takeoffLayers: v };
            }));
          }
          // visibleLayers: { [layerId]: bool } — lokalė, nesikeičia tarp sesijų
          const [visibleLayers, setVisibleLayers] = useState({});
          const [activeLayerId, setActiveLayerId] = useState(null);
          function isLayerVisible(layerId) {
            return visibleLayers[layerId] === true;
          }
          function addLayer(systemKey) {
            const preset = SYSTEM_PRESETS.find(s => s.key === systemKey);
            if (!preset) return;
            const existing = layers.filter(l => l.drawingId === selectedDrawingId && l.systemKey === systemKey);
            const nr = existing.length + 1;
            const name = existing.length === 0 ? preset.label : `${preset.label} ${nr}`;
            const nl = { id: Date.now(), systemKey, label: name, color: preset.color, drawingId: selectedDrawingId };
            setLayers(prev => [...prev, nl]);
            setActiveLayerId(nl.id);
            return nl;
          }


          // ─── Lokalė būsena ───
          const [selectedDrawingId, setSelectedDrawingId] = useState(null);
          // Sinchronizuoti su išoriniu pasirinkimu (iš ProjectsSidebar)
          useEffect(() => {
            if (externalSelectedDrawingId !== undefined && externalSelectedDrawingId !== selectedDrawingId) {
              setSelectedDrawingId(externalSelectedDrawingId);
            }
          }, [externalSelectedDrawingId]);
          const [zoom, setZoom] = useState(1);
          const [pan, setPan] = useState({ x: 0, y: 0 });
          const [isPanning, setIsPanning] = useState(false);
          const [panStart, setPanStart] = useState(null);
          const panMovedRef = useRef(false);
          const draggingRef = useRef(null);

          // PDF renderinimo mastelis — breakpoint-based (2,4,6,8), nereaguoja į kiekvieną zoom žingsnį
          const [pdfRenderScale, setPdfRenderScale] = useState(2);
          useEffect(() => {
            const needed = Math.max(2, Math.min(8, Math.ceil(zoom / 1.2) * 2));
            if (needed > pdfRenderScale) setPdfRenderScale(needed);
            else if (needed < pdfRenderScale - 2) setPdfRenderScale(needed);
          }, [zoom]);

          // Režimai: 'select' | 'calibrate' | 'draw' | 'templates'
          const [mode, setMode] = useState('select');
          const [activeTemplateId, setActiveTemplateId] = useState(null);

          // Piešimo būsena
          const [drawingPoints, setDrawingPoints] = useState([]); // koordinatės turinio erdvėje
          const [mousePos, setMousePos] = useState(null); // laikina pelės pozicija

          // Kalibravimas
          const [calibPoints, setCalibPoints] = useState([]);
          const [calibRealLength, setCalibRealLength] = useState('');
          const [showCalibInput, setShowCalibInput] = useState(false);

          // ─── Atskaitos linija (kalibravimas) — saugoma atskirai kiekvienam brėžiniui ───
          const calibrations = useMemo(() => activeProject ? (activeProject.takeoffCalibrations || {}) : {}, [activeProject]);
          const calibration = selectedDrawingId ? (calibrations[selectedDrawingId] || null) : null;
          function setCalibration(val) {
            if (!selectedDrawingId) return;
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffCalibrations || {};
              return { ...p, takeoffCalibrations: { ...cur, [selectedDrawingId]: val } };
            }));
          }

          // Sluoksniai — computed after selectedDrawingId
          const drawingLayers = selectedDrawingId ? layers.filter(l => l.drawingId === selectedDrawingId) : [];
          const activeLayer = layers.find(l => l.id === activeLayerId) || null;
          const canDraw = true;

          // ─── Patalpos ───
          const ROOM_PALETTE = ['#e53935','#8e24aa','#1e88e5','#00897b','#f4511e','#6d4c41','#00acc1','#43a047','#fb8c00','#3949ab','#d81b60','#546e7a'];
          const ROOM_SNAP_PX = 12; // ekrano pikseliais — kiek arti reikia paspausti esamo taško, kad prie jo "prisiristų"
          // Kiekviena grindinio šildymo zona gauna skirtingą spalvą iš šios paletės (ne visos vieną sluoksnio spalvą)
          const FLOOR_ZONE_PALETTE = ['#f59e0b','#0891b2','#7c3aed','#db2777','#16a34a','#dc2626','#2563eb','#ca8a04','#059669','#9333ea'];
          const rooms = useMemo(() => activeProject ? (activeProject.takeoffRooms || []) : [], [activeProject]);
          function setRooms(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffRooms || [];
              const v = typeof val === 'function' ? val(cur) : val;
              return { ...p, takeoffRooms: v };
            }));
          }
          // Patalpos režimo būsena (lokali)
          const [roomMode, setRoomMode] = useState(false);
          const [roomsVisible, setRoomsVisible] = useState(true);
          const [roomPoints, setRoomPoints] = useState([]);
          const [highlightedRoomId, setHighlightedRoomId] = useState(null);
          const [renamingRoomId, setRenamingRoomId] = useState(null);
          const [renameRoomVal, setRenameRoomVal] = useState('');
          function saveRoomRename(id) {
            if (!renameRoomVal.trim()) { setRenamingRoomId(null); return; }
            setRooms(prev => prev.map(r => r.id === id ? {...r, name: renameRoomVal.trim()} : r));
            setRenamingRoomId(null);
          }

          // Grindinio šildymo zonos — state (saugomas projekte)
          const floorZones = useMemo(() => activeProject ? (activeProject.takeoffFloorZones || []) : [], [activeProject]);
          function setFloorZones(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffFloorZones || [];
              const v = typeof val === 'function' ? val(cur) : val;
              return { ...p, takeoffFloorZones: v };
            }));
          }

          // Šildymo nustatymai — saugomi projekte (nepamirštama)
          const heatSettings = useMemo(() => activeProject ? (activeProject.takeoffHeatSettings || {}) : {}, [activeProject]);
          function setHeatSettings(val) {
            setProjects(prev => prev.map(p => {
              if (p.id !== activeProjectId) return p;
              const cur = p.takeoffHeatSettings || {};
              const v = typeof val === 'function' ? val(cur) : val;
              return { ...p, takeoffHeatSettings: v };
            }));
          }
          // Per-layer šildymo nustatymai: heatSettings[layerId] = { heatType, heatClass, divContours, divPipe }
          function getLayerHeat(layerId) {
            return heatSettings[layerId] || { heatType: null, heatClass: 'B', divContours: 12, divPipe: 7 };
          }
          function setLayerHeat(layerId, patch) {
            setHeatSettings(prev => ({...prev, [layerId]: {...(prev[layerId]||{heatType:null,heatClass:'B',divContours:12,divPipe:7}), ...patch}}));
          }
          const [floorZoneMode, setFloorZoneMode] = useState(false); // piešiame grindinio zonos kontūrą
          const [floorZonePoints, setFloorZonePoints] = useState([]);
          const [floorZoneLayerId, setFloorZoneLayerId] = useState(null); // kuriam sluoksniui priskiriama zona
          const [floorZonesVisible, setFloorZonesVisible] = useState(true);
          const drawingFloorZones = selectedDrawingId ? floorZones.filter(z => z.drawingId === selectedDrawingId) : [];
          const [pendingRoomName, setPendingRoomName] = useState('');
          const [showRoomNameInput, setShowRoomNameInput] = useState(false);
          const [roomCeilingHeight, setRoomCeilingHeight] = useState('2.70'); // m, bendras visiems brėžiniams
          const drawingRooms = selectedDrawingId ? rooms.filter(r => r.drawingId === selectedDrawingId) : [];

          // Apskaičiuoti plotą (Shoelace formula, pikseliais → metrai)
          function calcRoomArea(points, scale) {
            if (!points || points.length < 3) return null;
            let area = 0;
            const n = points.length;
            for (let i = 0; i < n; i++) {
              const j = (i + 1) % n;
              area += points[i].x * points[j].y;
              area -= points[j].x * points[i].y;
            }
            const areaPx = Math.abs(area) / 2;
            if (!scale) return { px: areaPx, m2: null };
            const areaM2 = areaPx * scale * scale;
            return { px: areaPx, m2: areaM2 };
          }

          function selectDrawing(id) {
            setSelectedDrawingId(id);
            setZoom(1); setPan({x:0,y:0});
            if (onSelectDrawing) onSelectDrawing(id);
          }

          function finishRoom(points) {
            if (!selectedDrawing || points.length < 3) return;
            const scale = getScale();
            const areaData = calcRoomArea(points, scale);
            const colorIdx = rooms.length % ROOM_PALETTE.length;
            const nr = drawingRooms.length + 1;
            const room = {
              id: Date.now(),
              drawingId: selectedDrawing.id,
              name: pendingRoomName.trim() || `Patalpa ${nr}`,
              points: [...points],
              color: ROOM_PALETTE[colorIdx],
              areaPx: areaData ? areaData.px : null,
              areaM2: areaData ? areaData.m2 : null,
            };
            setRooms(prev => [...prev, room]);
            setRoomPoints([]);
            setRoomMode(false);
            setPendingRoomName('');
            setShowRoomNameInput(false);
          }

          function finishFloorZone(points) {
            if (!selectedDrawing || points.length < 3) return;
            const scale = getScale();
            const areaData = calcRoomArea(points, scale);
            const areaM2 = areaData && areaData.m2 ? r2(areaData.m2) : null;
            const nr = drawingFloorZones.filter(z => z.layerId === floorZoneLayerId).length + 1;
            const zone = {
              id: Date.now(),
              drawingId: selectedDrawing.id,
              layerId: floorZoneLayerId,
              label: `Zona ${nr}`,
              points: [...points],
              areaPx: areaData ? areaData.px : null,
              areaM2,
            };
            setFloorZones(prev => [...prev, zone]);
            setFloorZonePoints([]);
            setFloorZoneMode(false);
            setFloorZoneLayerId(null);
          }

          // Šablonų redaktorius
          const [showTemplateEditor, setShowTemplateEditor] = useState(false);
          const [editingTemplate, setEditingTemplate] = useState(null);
          // Išskleistų šablonų grupių sąrašas (išlieka tarp render'ų)
          const [openTemplateGroups, setOpenTemplateGroups] = useState({});
          function toggleTemplateGroup(name) {
            setOpenTemplateGroups(prev => ({...prev, [name]: !prev[name]}));
          }

          // Žymėjimo info
          const [selectedMarkingId, setSelectedMarkingId] = useState(null);

          // Patalpos paryškinimas sąraše — nusiima perėjus prie kito veiksmo
          // (kito žymėjimo, režimo, brėžinio ar patalpos/zonos piešimo pradžios).
          useEffect(() => {
            setHighlightedRoomId(null);
          }, [selectedMarkingId, mode, roomMode, floorZoneMode, selectedDrawingId]);

          // Suvestinė
          const [showSummary, setShowSummary] = useState(false);
          const [templatesOpen, setTemplatesOpen] = useState(false);
          const [roomsOpen, setRoomsOpen] = useState(false);
          const [showLayersDropdown, setShowLayersDropdown] = useState(false);

          // PDF eksportas
          const [showExport, setShowExport] = useState(false);
          const [showMultiExport, setShowMultiExport] = useState(false);
          const [multiExportDrawings, setMultiExportDrawings] = useState([]);
          const [pdfStructure, setPdfStructure] = useState('grouped'); // 'grouped' | 'paired'
          const [drawingLabels, setDrawingLabels] = useState(() => {
            try { return JSON.parse(localStorage.getItem('amteka_drawingLabels')||'{}'); } catch(e){ return {}; }
          });
          // Išsaugoti drawingLabels kai keičiasi
          React.useEffect(() => {
            try { localStorage.setItem('amteka_drawingLabels', JSON.stringify(drawingLabels)); } catch(e){}
          }, [drawingLabels]);
          const [exportPeriod, setExportPeriod] = useState('month'); // 'week'|'month'|'custom'
          const [exportFileName, setExportFileName] = useState('');
          const [exportDateFrom, setExportDateFrom] = useState(() => {
            const d = new Date(); d.setDate(1);
            return d.toISOString().split('T')[0];
          });
          const [exportDateTo, setExportDateTo] = useState(() => {
            const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth()+1, 0);
            return last.toISOString().split('T')[0];
          });
          const [exporting, setExporting] = useState(false);

          // PDF įkėlimas
          const [pdfDetecting, setPdfDetecting] = useState(false); // skaičiuojami puslapiai prieš patvirtinimą
          const [pdfPendingSplit, setPdfPendingSplit] = useState(null); // { file, buf, baseName, numPages } laukiantis patvirtinimo
          const [idbPdfs, setIdbPdfs] = useState({});
          const loadedPdfIds = useRef(new Set());
          const [pdfPages, setPdfPages] = useState({}); // drawingId -> [page, ...]
          const [pageSize, setPageSize] = useState({}); // drawingId -> { w, h }
          const [pdfLoadErrors, setPdfLoadErrors] = useState({}); // drawingId -> error message
          const [pdfRetryTick, setPdfRetryTick] = useState(0); // pakeitus priverčia efektą bandyti iš naujo
          const containerRef = useRef(null);
          const _internalFileInputRef = useRef(null);
          const fileInputRef = externalFileInputRef || _internalFileInputRef;

          // Auto-pasirinkti pirmą
          useEffect(() => {
            if (!selectedDrawingId && drawings.length > 0) setSelectedDrawingId(drawings[0].id);
          }, [drawings.length]);

          // Krauname PDF iš IndexedDB (greitas kelias); jei lokaliai nėra, o
          // brėžinys turi serverId (pvz. atidarius projektą kitame naršyklėje),
          // parsisiunčiame iš serverio ir įrašome lokaliai vėlesniems kartams.
          useEffect(() => {
            drawings.forEach(async d => {
              if (loadedPdfIds.current.has(d.id)) return;
              loadedPdfIds.current.add(d.id);
              try {
                let buf = await TAKEOFF_DB.load(d.id);
                if (!buf && d.serverId) buf = await fetchAndCacheDrawing(d.id, d.serverId);
                if (buf) setIdbPdfs(prev => ({ ...prev, [d.id]: buf }));
                else loadedPdfIds.current.delete(d.id);
              } catch(e) { loadedPdfIds.current.delete(d.id); }
            });
          }, [drawings]);

          // Renderinti PDF puslapius kai turime buferį
          useEffect(() => {
            Object.entries(idbPdfs).forEach(([idStr, buf]) => {
              const id = parseInt(idStr);
              if (pdfPages[id] || pdfLoadErrors[id]) return;
              const lib = pdfjsLib;
              if (!lib) return;
              if (!lib.GlobalWorkerOptions.workerSrc)
                lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
              // Rasti drawing objektą kad žinotume pageNum
              const drawing = drawings.find(d => d.id === id);
              const pageNum = (drawing && drawing.pageNum) ? drawing.pageNum : 1;
              const copy = buf.slice(0);
              const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Baigėsi laukimo laikas')), 20000));
              Promise.race([lib.getDocument({ data: copy }).promise, timeout]).then(async doc => {
                const pg = await doc.getPage(pageNum);
                const vp2 = pg.getViewport({ scale: 2 });
                setPageSize(prev => ({ ...prev, [id]: { w: Math.ceil(vp2.width), h: Math.ceil(vp2.height) } }));
                setPdfPages(prev => ({ ...prev, [id]: [pg] }));
              }).catch(e => {
                console.error('PDF load:', e);
                setPdfLoadErrors(prev => ({ ...prev, [id]: (e && e.message) || 'Nepavyko įkelti PDF' }));
              });
            });
          }, [idbPdfs, drawings, pdfRetryTick]);

          // Kainininkas pozicijos flat (ziniarastis = kainininkai prop)
          const allZinItems = useMemo(() => {
            const items = [];
            (ziniarastis || []).forEach(kain => {
              (kain.groups || []).forEach(g => {
                (g.items || []).forEach(it => items.push({ ...it, groupName: g.name, groupId: g.id, kainName: kain.name, kainId: kain.id }));
              });
            });
            return items;
          }, [ziniarastis]);

          const selectedDrawing = drawings.find(d => d.id === selectedDrawingId) || null;
          const currentPages = selectedDrawing ? (pdfPages[selectedDrawing.id] || []) : [];
          const currentPageSize = selectedDrawing ? (pageSize[selectedDrawing.id] || { w: 800, h: 1000 }) : { w: 800, h: 1000 };
          const activeTemplate = templates.find(t => t.id === activeTemplateId) || null;

          // ─── Konversijos: ekrano ↔ turinys ───
          function screenToContent(sx, sy) {
            return { x: (sx - pan.x) / zoom, y: (sy - pan.y) / zoom };
          }

          // ─── Kalibravimo mastelis ───
          function getScale() {
            if (!calibration || !calibration.pixelLength || !calibration.realLength) return null;
            return calibration.realLength / calibration.pixelLength; // m / px
          }
          function pxToReal(px) {
            const s = getScale();
            return s ? px * s : null;
          }

          // ─── Geometrija ───
          function lineLength(pts) {
            let len = 0;
            for (let i = 1; i < pts.length; i++) {
              const dx = pts[i].x - pts[i-1].x, dy = pts[i].y - pts[i-1].y;
              len += Math.sqrt(dx*dx + dy*dy);
            }
            return len;
          }

          function computeMarkingValue(marking) {
            // Grąžina skaičiavimo reikšmę pagal formą
            if (marking.shape === 'line' || marking.shape === 'polyline') {
              return lineLength(marking.points); // px
            } else {
              return 1; // apskritimas, stačiakampis, trikampis → 1 vnt
            }
          }

          // ─── Suvestinė ───
          const summary = useMemo(() => {
            const scale = getScale();
            const perTemplate = {};
            // Filtruoti tik aktyvaus brėžinio žymėjimus
            const activeMarkings = markings.filter(m => m.drawingId === selectedDrawingId);
            activeMarkings.forEach(m => {
              const tmpl = templates.find(t => t.id === m.templateId);
              if (!tmpl) return;
              const rawVal = computeMarkingValue(m);
              if (!perTemplate[tmpl.id]) perTemplate[tmpl.id] = { tmpl, items: {}, count: 0 };
              perTemplate[tmpl.id].count++;
              (tmpl.positions || []).forEach(pos => {
                const key = pos.id || pos.itemId || pos.name;
                if (!perTemplate[tmpl.id].items[key])
                  perTemplate[tmpl.id].items[key] = { pos, total: 0 };
                let qty = 0;
                if (pos.unit === 'vnt.' || tmpl.shape !== 'line' && tmpl.shape !== 'polyline') {
                  qty = pos.qtyPerUnit * 1;
                } else {
                  const realLen = scale ? rawVal * scale : rawVal;
                  qty = realLen * pos.qtyPerUnit;
                }
                perTemplate[tmpl.id].items[key].total += qty;
              });
            });

            return Object.values(perTemplate);
          }, [markings, templates, calibration, selectedDrawingId]);

          // ─── Perkėlimas į Atl. darbus ───
          function transferToAtlikti() {
            const todayStr = new Date().toISOString().split('T')[0];
            const scale = getScale();
            const newItems = [];
            markings.forEach(m => {
              const tmpl = templates.find(t => t.id === m.templateId);
              if (!tmpl) return;
              const rawVal = computeMarkingValue(m);
              (tmpl.positions || []).forEach(pos => {
                let qty = 0;
                if (tmpl.shape === 'line' || tmpl.shape === 'polyline') {
                  const realLen = scale ? rawVal * scale : rawVal;
                  qty = realLen * pos.qtyPerUnit;
                } else {
                  qty = pos.qtyPerUnit;
                }
                newItems.push({ itemId: pos.itemId, name: pos.name, unit: pos.unit, price: pos.price || 0, plannedQty: 0, qty: parseFloat(qty.toFixed(4)), groupName: pos.groupName, groupId: pos.groupId });
              });
            });
            // Sugrupuoti pagal itemId
            const grouped = {};
            newItems.forEach(it => {
              if (!grouped[it.itemId]) grouped[it.itemId] = { ...it, qty: 0 };
              grouped[it.itemId].qty += it.qty;
            });
            const combinedItems = Object.values(grouped).map(it => ({ ...it, qty: parseFloat(it.qty.toFixed(4)) }));
            if (combinedItems.length === 0) { alert('Nėra sužymėtų elementų.'); return; }
            const entry = {
              id: Date.now(),
              date: todayStr,
              taskId: '',
              workers: [],
              items: combinedItems,
              notes: 'Automatiškai iš Takeoff sužymėjimų',
            };
            setAtliktiDarbai([...(atliktiDarbai || []), entry]);
            alert('Perkelta į Atliktus darbus (' + combinedItems.length + ' pozicijų)!');
          }

          // ─── PDF generavimas ───
          async function generatePdf(dateFrom, dateTo, reportTitle) {
            if (!selectedDrawing) { alert('Pasirinkite brėžinį.'); return; }
            const buf = idbPdfs[selectedDrawing.id];
            if (!buf) { alert('Brėžinys dar neįkrautas.'); return; }
            setExporting(true);
            try {

              // Lietuviškų raidžių helper
              function lt(text) {
                return String(text).replace(/[\u0104\u010C\u0118\u0116\u012E\u0160\u0172\u016A\u017D]/g, c => ({
                  '\u0104':'A','\u010C':'C','\u0118':'E','\u0116':'E','\u012E':'I','\u0160':'S','\u0172':'U','\u016A':'U','\u017D':'Z'
                }[c]||c)).replace(/[\u0105\u010D\u0119\u0117\u012F\u0161\u0173\u016B\u017E]/g, c => ({
                  '\u0105':'a','\u010D':'c','\u0119':'e','\u0117':'e','\u012F':'i','\u0161':'s','\u0173':'u','\u016B':'u','\u017E':'z'
                }[c]||c));
              }

              const filteredMarkings = markings.filter(m =>
                m.drawingId === selectedDrawing.id &&
                m.createdAt >= dateFrom && m.createdAt <= dateTo
              );

              // ── 1 LAPAS: Brėžinys su žymėjimais ──
              const lib = pdfjsLib;
              if (!lib) throw new Error('PDF.js nepasiekiamas');

              const pdfDoc = await lib.getDocument({ data: buf.slice(0) }).promise;
              const pg = await pdfDoc.getPage(selectedDrawing.pageNum || 1);

              const A3W_PT = 1190, A3H_PT = 841;
              const vpNat = pg.getViewport({ scale: 1 });
              const RENDER_SCALE = 3;
              const fitScale = Math.min(A3W_PT / vpNat.width, A3H_PT / vpNat.height) * RENDER_SCALE;
              const vp = pg.getViewport({ scale: fitScale });

              const offCanvas = document.createElement('canvas');
              offCanvas.width = Math.ceil(vp.width);
              offCanvas.height = Math.ceil(vp.height);
              const ctx = offCanvas.getContext('2d');
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, offCanvas.width, offCanvas.height);
              await pg.render({ canvasContext: ctx, viewport: vp }).promise;

              const DPR = window.devicePixelRatio || 1;
              const contentToCanvas = fitScale / (2 / DPR);

              // Nupiešti žymėjimus
              filteredMarkings.forEach(m => {
                const tmpl = templates.find(t => t.id === m.templateId);
                if (!tmpl || !m.points || m.points.length === 0) return;
                const col = tmpl.color || '#735DFF';
                const sw = (tmpl.strokeWidth || 2) * contentToCanvas;
                const sz = (tmpl.size || 10) * contentToCanvas;
                ctx.save();
                ctx.strokeStyle = col; ctx.fillStyle = col;
                ctx.lineWidth = sw; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
                const px = p => p.x * contentToCanvas;
                const py = p => p.y * contentToCanvas;
                if (m.shape === 'line' && m.points.length >= 2) {
                  ctx.beginPath(); ctx.moveTo(px(m.points[0]), py(m.points[0]));
                  ctx.lineTo(px(m.points[m.points.length-1]), py(m.points[m.points.length-1]));
                  ctx.stroke();
                } else if (m.shape === 'polyline' && m.points.length >= 2) {
                  ctx.beginPath(); ctx.moveTo(px(m.points[0]), py(m.points[0]));
                  m.points.slice(1).forEach(p => ctx.lineTo(px(p), py(p))); ctx.stroke();
                } else if (m.points.length > 0) {
                  const cx2=px(m.points[0]), cy2=py(m.points[0]);
                  const polyPath=(n,r,off=0)=>{ctx.beginPath();for(let i=0;i<n;i++){const a=2*Math.PI/n*i+off-Math.PI/2;i===0?ctx.moveTo(cx2+Math.cos(a)*r,cy2+Math.sin(a)*r):ctx.lineTo(cx2+Math.cos(a)*r,cy2+Math.sin(a)*r);}ctx.closePath();};
                  const starPath=(n,r1,r2)=>{ctx.beginPath();for(let i=0;i<n*2;i++){const a=Math.PI/n*i-Math.PI/2;const r=i%2===0?r1:r2;i===0?ctx.moveTo(cx2+Math.cos(a)*r,cy2+Math.sin(a)*r):ctx.lineTo(cx2+Math.cos(a)*r,cy2+Math.sin(a)*r);}ctx.closePath();};
                  if (m.shape==='circle'){ctx.beginPath();ctx.arc(cx2,cy2,sz,0,Math.PI*2);ctx.fill();}
                  else if(m.shape==='rect'){ctx.fillRect(cx2-sz/2,cy2-sz/2,sz,sz);}
                  else if(m.shape==='triangle'){ctx.beginPath();ctx.moveTo(cx2,cy2-sz);ctx.lineTo(cx2-sz*0.866,cy2+sz*0.5);ctx.lineTo(cx2+sz*0.866,cy2+sz*0.5);ctx.closePath();ctx.fill();}
                  else if(m.shape==='triangle_down'){ctx.beginPath();ctx.moveTo(cx2,cy2+sz);ctx.lineTo(cx2-sz*0.866,cy2-sz*0.5);ctx.lineTo(cx2+sz*0.866,cy2-sz*0.5);ctx.closePath();ctx.fill();}
                  else if(m.shape==='diamond'){ctx.beginPath();ctx.moveTo(cx2,cy2-sz);ctx.lineTo(cx2+sz*0.75,cy2);ctx.lineTo(cx2,cy2+sz);ctx.lineTo(cx2-sz*0.75,cy2);ctx.closePath();ctx.fill();}
                  else if(m.shape==='star'){starPath(5,sz,sz*0.45);ctx.fill();}
                  else if(m.shape==='star6'){starPath(6,sz,sz*0.5);ctx.fill();}
                  else if(m.shape==='cross'){ctx.fillRect(cx2-sz/4,cy2-sz,sz/2,sz*2);ctx.fillRect(cx2-sz,cy2-sz/4,sz*2,sz/2);}
                  else if(m.shape==='pentagon'){polyPath(5,sz);ctx.fill();}
                  else if(m.shape==='hexagon'){polyPath(6,sz);ctx.fill();}
                  else if(m.shape==='octagon'){polyPath(8,sz);ctx.fill();}
                  else if(m.shape==='arrow_right'){ctx.beginPath();ctx.moveTo(cx2-sz,cy2-sz*0.35);ctx.lineTo(cx2+sz*0.3,cy2-sz*0.35);ctx.lineTo(cx2+sz*0.3,cy2-sz*0.7);ctx.lineTo(cx2+sz,cy2);ctx.lineTo(cx2+sz*0.3,cy2+sz*0.7);ctx.lineTo(cx2+sz*0.3,cy2+sz*0.35);ctx.lineTo(cx2-sz,cy2+sz*0.35);ctx.closePath();ctx.fill();}
                  else if(m.shape==='arrow_left'){ctx.beginPath();ctx.moveTo(cx2+sz,cy2-sz*0.35);ctx.lineTo(cx2-sz*0.3,cy2-sz*0.35);ctx.lineTo(cx2-sz*0.3,cy2-sz*0.7);ctx.lineTo(cx2-sz,cy2);ctx.lineTo(cx2-sz*0.3,cy2+sz*0.7);ctx.lineTo(cx2-sz*0.3,cy2+sz*0.35);ctx.lineTo(cx2+sz,cy2+sz*0.35);ctx.closePath();ctx.fill();}
                  else if(m.shape==='arrow_both'){ctx.beginPath();ctx.moveTo(cx2-sz,cy2);ctx.lineTo(cx2-sz*0.4,cy2-sz*0.6);ctx.lineTo(cx2-sz*0.4,cy2-sz*0.3);ctx.lineTo(cx2+sz*0.4,cy2-sz*0.3);ctx.lineTo(cx2+sz*0.4,cy2-sz*0.6);ctx.lineTo(cx2+sz,cy2);ctx.lineTo(cx2+sz*0.4,cy2+sz*0.6);ctx.lineTo(cx2+sz*0.4,cy2+sz*0.3);ctx.lineTo(cx2-sz*0.4,cy2+sz*0.3);ctx.lineTo(cx2-sz*0.4,cy2+sz*0.6);ctx.closePath();ctx.fill();}
                  else if(m.shape==='circle_cross'){ctx.beginPath();ctx.arc(cx2,cy2,sz,0,Math.PI*2);ctx.stroke();ctx.beginPath();ctx.moveTo(cx2-sz,cy2);ctx.lineTo(cx2+sz,cy2);ctx.stroke();ctx.beginPath();ctx.moveTo(cx2,cy2-sz);ctx.lineTo(cx2,cy2+sz);ctx.stroke();}
                  else if(m.shape==='rect_cross'){ctx.strokeRect(cx2-sz,cy2-sz,sz*2,sz*2);ctx.beginPath();ctx.moveTo(cx2-sz,cy2-sz);ctx.lineTo(cx2+sz,cy2+sz);ctx.stroke();ctx.beginPath();ctx.moveTo(cx2+sz,cy2-sz);ctx.lineTo(cx2-sz,cy2+sz);ctx.stroke();}
                  else if(m.shape==='double_circle'){ctx.beginPath();ctx.arc(cx2,cy2,sz,0,Math.PI*2);ctx.stroke();ctx.beginPath();ctx.arc(cx2,cy2,sz*0.55,0,Math.PI*2);ctx.fill();}
                  else if(m.shape==='ellipse'){ctx.beginPath();ctx.ellipse(cx2,cy2,sz*1.5,sz*0.7,0,0,Math.PI*2);ctx.fill();}
                }
                ctx.restore();
              });

              // Legenda
              const usedTmplIds = [...new Set(filteredMarkings.map(m => m.templateId))];
              const usedTmpls = usedTmplIds.map(id => templates.find(t => t.id === id)).filter(Boolean);
              if (usedTmpls.length > 0) {
                const legendX = offCanvas.width - 10*contentToCanvas;
                const legendY = 5*contentToCanvas;
                const lineH = 3.5*contentToCanvas, boxW = 30*contentToCanvas;
                const boxH = (usedTmpls.length+1)*lineH + lineH;
                ctx.save();
                ctx.fillStyle='rgba(255,255,255,0.88)';
                ctx.fillRect(legendX-boxW-lineH, legendY-lineH/2, boxW+lineH*2, boxH);
                ctx.strokeStyle='#cccccc'; ctx.lineWidth=0.5*contentToCanvas;
                ctx.strokeRect(legendX-boxW-lineH, legendY-lineH/2, boxW+lineH*2, boxH);
                ctx.fillStyle='#1c1c1c'; ctx.textAlign='left';
                ctx.font='bold '+(2.2*contentToCanvas)+'px Arial';
                ctx.fillText('Legenda', legendX-boxW, legendY+lineH*0.7);
                usedTmpls.forEach((tmpl,i) => {
                  const y = legendY+lineH*(i+1.8);
                  ctx.fillStyle=tmpl.color||'#735DFF';
                  ctx.fillRect(legendX-boxW, y-lineH*0.4, lineH*0.7, lineH*0.7);
                  ctx.fillStyle='#1c1c1c'; ctx.font=(2*contentToCanvas)+'px Arial';
                  ctx.fillText(lt(tmpl.name), legendX-boxW+lineH, y);
                });
                ctx.restore();
              }

              // Canvas → PDF
              // JPEG 0.95: vizualiai identiškas PNG, bet ~6× mažesnis failas
              const imgData = offCanvas.toDataURL('image/jpeg', 0.95);
              const canvasAspect = offCanvas.width / offCanvas.height;
              let imgW = A3W_PT, imgH = A3W_PT / canvasAspect;
              if (imgH > A3H_PT) { imgH = A3H_PT; imgW = A3H_PT * canvasAspect; }
              const imgX = (A3W_PT - imgW) / 2;

              const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3', compress: true });

              // 1 lapo antraštė
              const projTitle = activeProject ? (activeProject.title || 'Projektas') : 'Projektas';
              const headerLine = reportTitle && reportTitle.trim()
                ? lt(reportTitle.trim()) + '  ·  ' + dateFrom + ' – ' + dateTo
                : lt(projTitle) + '  ·  ' + lt(selectedDrawing.name) + '  ·  ' + dateFrom + ' – ' + dateTo;
              pdf.setFontSize(10); pdf.setTextColor(89, 97, 99);
              pdf.text(headerLine, 20, 18, { maxWidth: A3W_PT - 40 });
              pdf.setTextColor(0, 0, 0);
              pdf.addImage(imgData, 'JPEG', imgX, 24, imgW, imgH - 24);

              // ── 2 LAPAS: Kiekių suvestinė ──
              pdf.addPage('a4', 'portrait');
              const A4W = 595, A4H = 842;
              const mL = 28, mR = 28, mT = 57, mB = 57;
              let curY = mT;
              const ADDRESS = 'Gamyklos g. 7, Maceiniai';

              // Antraštė
              const titleLine = reportTitle && reportTitle.trim() ? lt(reportTitle.trim()) : 'Takeoff suvestine';
              pdf.setFontSize(16); pdf.setFont('helvetica','bold'); pdf.setTextColor(28,28,28);
              { const tLines = pdf.splitTextToSize(titleLine, A4W-mL-mR);
                pdf.text(tLines, mL, curY); curY += tLines.length * 18 + 2; }
              pdf.setFontSize(9); pdf.setFont('helvetica','normal'); pdf.setTextColor(89,97,99);
              { const aLine = ADDRESS + '  ·  ' + lt(selectedDrawing.name);
                const aLines = pdf.splitTextToSize(aLine, A4W-mL-mR);
                pdf.text(aLines, mL, curY); curY += aLines.length * 12 + 1; }
              pdf.text('Laikotarpis: ' + dateFrom + ' - ' + dateTo, mL, curY); curY += 16;

              // Apskaičiuoti kiekius, sugrupuoti pagal žiniaraščio grupę
              const scale = getScale();
              const groupedMap = {};
              filteredMarkings.forEach(m => {
                const tmpl = templates.find(t => t.id === m.templateId);
                if (!tmpl) return;
                const rawVal = computeMarkingValue(m);
                (tmpl.positions || []).forEach(pos => {
                  const gId = String(pos.groupId || 'kita');
                  const gName = pos.groupName || 'Kita';
                  if (!groupedMap[gId]) groupedMap[gId] = { groupName: gName, items: {} };
                  const key = String(pos.itemId || pos.name);
                  if (!groupedMap[gId].items[key])
                    groupedMap[gId].items[key] = { pos: { ...pos, tmplName: tmpl.name }, total: 0 };
                  const isLinear = tmpl.shape === 'line' || tmpl.shape === 'polyline';
                  let qty = 0;
                  if (pos.unit === 'vnt.' || !isLinear) {
                    qty = pos.qtyPerUnit * 1;
                  } else {
                    const realLen = scale ? rawVal * scale : rawVal;
                    qty = realLen * pos.qtyPerUnit;
                  }
                  groupedMap[gId].items[key].total += qty;
                });
              });

              // Eilės numeriai iš kainininko
              Object.entries(groupedMap).forEach(([gId, grp]) => {
                const zinItems = allZinItems.filter(it => String(it.groupId) === gId);
                Object.values(grp.items).forEach(entry => {
                  const idx = zinItems.findIndex(it => String(it.id) === String(entry.pos.itemId));
                  entry.zinNr = idx >= 0 ? idx + 1 : null;
                });
              });

              const allGroupsArr = Object.values(groupedMap);
              const totalItems = allGroupsArr.reduce((a,g) => a + Object.values(g.items).length, 0);

              if (totalItems === 0) {
                pdf.setFontSize(10); pdf.setTextColor(150,150,150);
                pdf.text('Nera suzymejumu pasirinktu laikotarpiu.', mL, curY);
              } else {
                const tableW = A4W - mL - mR;
                const colW = [22, tableW-22-42-55, 42, 55];
                const colX = [mL];
                colW.forEach((w,i) => colX.push(colX[i]+w));
                const groupHdrH = 14, hdrRowH = 13;
                const fontSize = 7.5, lineSpacing = 9.5, padV = 4;
                const headers = ['Nr.', 'Pozicija (ziniarastis)', 'Mato vnt.', 'Kiekis'];

                // Apskaičiuoja eilutės aukštį pagal teksto ilgį pozicijos stulpelyje
                function calcRowH(nameText) {
                  pdf.setFontSize(fontSize); pdf.setFont('helvetica','normal');
                  const lines = pdf.splitTextToSize(String(nameText), colW[1] - 6);
                  return Math.max(hdrRowH, lines.length * lineSpacing + padV);
                }

                function calcGrpHdrH(grp, cont) {
                  pdf.setFontSize(8.5); pdf.setFont('helvetica','bold');
                  const gLines = pdf.splitTextToSize(lt(grp.groupName)+(cont?' (tesinys)':''), tableW-8);
                  return Math.max(groupHdrH, gLines.length * 10 + 4);
                }
                function drawGrpHdr(grp, cy, cont) {
                  const gh = calcGrpHdrH(grp, cont);
                  pdf.setFillColor(204,37,0); pdf.rect(mL, cy, tableW, gh, 'F');
                  pdf.setTextColor(255,255,255); pdf.setFontSize(8.5); pdf.setFont('helvetica','bold');
                  const gLines = pdf.splitTextToSize(lt(grp.groupName)+(cont?' (tesinys)':''), tableW-8);
                  gLines.forEach((l,i) => pdf.text(l, mL+4, cy+10+i*10));
                  cy += gh;
                  pdf.setFillColor(89,97,99); pdf.rect(mL, cy, tableW, hdrRowH, 'F');
                  pdf.setTextColor(255,255,255); pdf.setFontSize(fontSize); pdf.setFont('helvetica','bold');
                  headers.forEach((h,i) => {
                    const align = i>=2?'right':'left';
                    pdf.text(lt(h), i>=2?colX[i]+colW[i]-3:colX[i]+3, cy+8.5, {align});
                  });
                  return cy + hdrRowH;
                }

                allGroupsArr.forEach(grp => {
                  const grpItems = Object.values(grp.items);
                  if (curY + calcGrpHdrH(grp, false) + hdrRowH > A4H - mB) {
                    pdf.addPage('a4','portrait'); curY = mT;
                  }
                  curY = drawGrpHdr(grp, curY, false);
                  pdf.setFont('helvetica','normal');
                  grpItems.forEach((row, idx) => {
                    const nameText = lt(row.pos.name||'');
                    const thisRowH = calcRowH(nameText);
                    if (curY + thisRowH > A4H - mB) {
                      pdf.addPage('a4','portrait'); curY = mT;
                      curY = drawGrpHdr(grp, curY, true);
                      pdf.setFont('helvetica','normal');
                    }
                    const bg = idx%2===0 ? [248,249,251] : [255,255,255];
                    pdf.setFillColor(...bg); pdf.rect(mL, curY, tableW, thisRowH, 'F');
                    pdf.setDrawColor(220,220,220); pdf.rect(mL, curY, tableW, thisRowH, 'S');
                    pdf.setTextColor(28,28,28); pdf.setFontSize(fontSize);
                    const qtyStr = Number.isInteger(row.total) ? String(row.total) : parseFloat(row.total.toFixed(3)).toString();
                    const isLinear2 = ['line','polyline'].includes(templates.find(t=>t.name===row.pos.tmplName)?.shape);
                    const nrLabel = row.zinNr != null ? String(row.zinNr) : String(idx+1);
                    // Nr, Mato vnt, Kiekis — viena eilutė viduryje
                    [[0,nrLabel],[2,row.pos.unit||''],[3,qtyStr+(isLinear2&&!scale?' px':'')]].forEach(([i,cell])=>{
                      const align = i>=2?'right':'left';
                      pdf.text(String(cell), i>=2?colX[i]+colW[i]-3:colX[i]+3, curY+thisRowH/2+fontSize/2-1, {align});
                    });
                    // Pozicija — daugieilutis tekstas
                    const nameLines = pdf.splitTextToSize(nameText, colW[1]-6);
                    const textBlockH = nameLines.length * lineSpacing;
                    const textStartY = curY + (thisRowH - textBlockH) / 2 + lineSpacing * 0.75;
                    nameLines.forEach((line, li) => {
                      pdf.text(line, colX[1]+3, textStartY + li*lineSpacing);
                    });
                    curY += thisRowH;
                  });
                  curY += 5;
                });
                if (curY+16 > A4H-mB) { pdf.addPage('a4','portrait'); curY = mT; }
                pdf.setFont('helvetica','bold'); pdf.setFontSize(8); pdf.setTextColor(89,97,99);
                pdf.text('Is viso poziciju: ' + totalItems, mL, curY+4);
              }

              // Puslapio numeravimas
              const pageCount = pdf.getNumberOfPages();
              for (let i = 1; i <= pageCount; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8); pdf.setTextColor(150,150,150);
                if (i === 1) {
                  // A3 landscape puslapis
                  pdf.text(String(i)+' / '+String(pageCount), A3W_PT-20, A3H_PT-10, {align:'right'});
                  pdf.text('AMTEKA - Sugeneruota: '+new Date().toISOString().split('T')[0], 20, A3H_PT-10);
                } else {
                  // A4 portrait puslapis
                  pdf.text(String(i)+' / '+String(pageCount), A4W-mR, A4H-18, {align:'right'});
                  pdf.text('AMTEKA - Sugeneruota: '+new Date().toISOString().split('T')[0], mL, A4H-18);
                }
              }

              // Failo pavadinimas
              const safeName = reportTitle && reportTitle.trim()
                ? lt(reportTitle.trim()).replace(/[^a-zA-Z0-9 _-]/g,'').replace(/\s+/g,'_').substring(0,50)
                : lt(projTitle).replace(/[^a-zA-Z0-9_-]/g,'_').replace(/_+/g,'_').substring(0,40)+'_Takeoff_'+lt(selectedDrawing.name||'').replace(/[^a-zA-Z0-9_-]/g,'_').substring(0,25);
              pdf.save(safeName+'_'+dateFrom+'_'+dateTo+'.pdf');

            } catch(err) {
              console.error('PDF klaida:', err);
              alert('PDF generavimo klaida: ' + err.message);
            } finally {
              setExporting(false);
              setShowExport(false);
            }
          }

          // ─── Kelių brėžinių PDF suvestinė ───
          async function generateMultiPdf(drawingIds, dateFrom, dateTo, reportTitle, drawingLabels, pdfStructure) {
            if (!drawingIds || drawingIds.length === 0) { alert('Pasirinkite bent vieną brėžinį.'); return; }
            setExporting(true);
            try {
              function lt(text) {
                return String(text)
                  .replace(/[\u0105\u010d\u0119\u0117\u012f\u0161\u0173\u016b\u017e]/g, c=>({\u0105:'a',\u010d:'c',\u0119:'e',\u0117:'e',\u012f:'i',\u0161:'s',\u0173:'u',\u016b:'u',\u017e:'z'}[c]||c))
                  .replace(/[\u0104\u010c\u0118\u0116\u012e\u0160\u0172\u016a\u017d]/g, c=>({\u0104:'A',\u010c:'C',\u0118:'E',\u0116:'E',\u012e:'I',\u0160:'S',\u0172:'U',\u016a:'U',\u017d:'Z'}[c]||c));
              }
              function fmtQty(v){ return v==null||v===0?'':(Number.isInteger(v)?String(v):parseFloat(v.toFixed(3)).toString()); }

              const ADDRESS = 'Gamyklos g. 7, Maceiniai';
              const titleLine = reportTitle&&reportTitle.trim() ? lt(reportTitle.trim()) : 'UAB_AMTEKA_ISPILDOMOSIOS_BENDRA_SUVESTINE_' + new Date().toISOString().slice(0,7).replace('-','.');

              const sc = calibration ? calibration.realLength / calibration.pixelLength : null;
              const selDrawings = drawingIds.map(id => drawings.find(d=>d.id===id)).filter(Boolean);
              function getDrawingScale(d) {
                const cal = calibrations[d.id];
                return cal ? cal.realLength / cal.pixelLength : sc;
              }
              function getLabel(d) {
                const c = (drawingLabels||{})[d.id];
                return (c && c.trim()) ? c.trim() : d.name;
              }

              // ── Agregavimas visų brėžinių kiekių ──
              const groupedMap = {};
              drawingIds.forEach(did => {
                const drw = drawings.find(d=>d.id===did);
                const drwSc = drw ? getDrawingScale(drw) : sc;
                const filtered = markings.filter(m=>m.drawingId===did&&m.createdAt>=dateFrom&&m.createdAt<=dateTo);
                filtered.forEach(m => {
                  const tmpl=templates.find(t=>t.id===m.templateId); if(!tmpl) return;
                  const rawVal=computeMarkingValue(m);
                  const isLinear=tmpl.shape==='line'||tmpl.shape==='polyline';
                  (tmpl.positions||[]).forEach(pos => {
                    const gId=String(pos.groupId||'kita'), gName=pos.groupName||'Kita';
                    const key=String(pos.itemId||pos.name);
                    if(!groupedMap[gId]) groupedMap[gId]={groupName:gName,items:{}};
                    if(!groupedMap[gId].items[key]) groupedMap[gId].items[key]={pos:{...pos},byDrawing:{},total:0};
                    let qty=0;
                    if(pos.unit==='vnt.'||!isLinear){qty=pos.qtyPerUnit*1;}
                    else{qty=(drwSc?rawVal*drwSc:rawVal)*pos.qtyPerUnit;}
                    groupedMap[gId].items[key].byDrawing[did]=(groupedMap[gId].items[key].byDrawing[did]||0)+qty;
                    groupedMap[gId].items[key].total+=qty;
                  });
                });
              });
              Object.entries(groupedMap).forEach(([gId,grp])=>{
                const zi=allZinItems.filter(it=>String(it.groupId)===gId);
                Object.values(grp.items).forEach(e=>{
                  const ix=zi.findIndex(it=>String(it.id)===String(e.pos.itemId));
                  e.zinNr=ix>=0?ix+1:null;
                });
              });
              const allGroupsArr=Object.values(groupedMap);
              const totalItems=allGroupsArr.reduce((a,g)=>a+Object.values(g.items).length,0);

              // ── Bendros lentelės stulpelių konfigūracija ──
              const PW=841.89, PH=595.28, mL=28, mR=28, mT=50, mB=50;
              const tableW=PW-mL-mR;
              const nDr=selDrawings.length;
              const fixedW=18+30+38;
              const drColW=Math.min(45,Math.max(28,Math.floor((tableW-fixedW-120)/Math.max(nDr,1))));
              const posColW=tableW-fixedW-drColW*nDr;
              const sumCols=[
                {w:18,label:'Nr.',align:'left'},
                {w:posColW,label:'Pozicija',align:'left'},
                {w:30,label:'Vnt.',align:'right'},
                {w:38,label:'Suma',align:'right'},
                ...selDrawings.map(d=>({w:drColW,label:lt(getLabel(d)),align:'right',drawingId:d.id}))
              ];
              const sumColX=[mL];
              sumCols.forEach((c,i)=>sumColX.push(sumColX[i]+c.w));
              const fsSum=nDr>8?5.5:nDr>5?6:6.5;
              const lsSum=fsSum*1.4;
              const hdrRowHSum=fsSum+6;
              const groupHdrH=13;

              // ── Pagalbinės lentelės funkcijos ──
              const mL2=28,mR2=28,mT2=57,mB2=57;
              const A4W2=595,A4H2=842;
              const fsT=7.5, lsT=9.5, pvT=3, rHT=13;

              function cRowHT(nm){pdf.setFontSize(fsT);pdf.setFont('helvetica','normal');const l=pdf.splitTextToSize(nm,A4W2-mL2-mR2-22-42-55-6);return Math.max(rHT,l.length*lsT+pvT);}
              function cGHT(grp,cont,tw){pdf.setFontSize(8);pdf.setFont('helvetica','bold');const l=pdf.splitTextToSize(lt(grp.groupName)+(cont?' (tesinys)':''),tw-8);return Math.max(groupHdrH,l.length*10+4);}
              function dGHT(grp,cy,cont,tw,cx,cw,hdrs){
                const gh=cGHT(grp,cont,tw);
                pdf.setFillColor(204,37,0);pdf.rect(cx[0],cy,tw,gh,'F');
                pdf.setTextColor(255,255,255);pdf.setFontSize(8);pdf.setFont('helvetica','bold');
                pdf.splitTextToSize(lt(grp.groupName)+(cont?' (tesinys)':''),tw-8).forEach((l,i)=>pdf.text(l,cx[0]+4,cy+9+i*9));
                cy+=gh;
                pdf.setFillColor(89,97,99);pdf.rect(cx[0],cy,tw,rHT,'F');
                pdf.setTextColor(255,255,255);pdf.setFontSize(fsT);pdf.setFont('helvetica','bold');
                hdrs.forEach((h,i)=>{pdf.text(lt(h),i>=2?cx[i]+cw[i]-3:cx[i]+3,cy+8.5,{align:i>=2?'right':'left'});});
                return cy+rHT;
              }

              // ── PDF: pradedame nuo A3 brėžinių ──
              const lib=pdfjsLib;
              let pdf;

              // Pirmojo puslapio formatas: A3 landscape (jei yra bent vienas brėžinys su buferiu)
              const firstDrawingWithBuf = selDrawings.find(d=>idbPdfs[d.id]);
              if (firstDrawingWithBuf) {
                pdf = new jsPDF({orientation:'landscape',unit:'pt',format:'a3',compress:true});
              } else {
                pdf = new jsPDF({orientation:'landscape',unit:'pt',format:'a4',compress:true});
              }
              let isFirstPage = true;

              // ── Pagalbinė funkcija: A4 kiekių lentelė vienam brėžiniui ──
              const renderDrawingTable = (drawing) => {
                const drawLabel = lt(getLabel(drawing));
                const drFiltered = markings.filter(m=>m.drawingId===drawing.id&&m.createdAt>=dateFrom&&m.createdAt<=dateTo);
                pdf.addPage('a4','portrait');
                let cy2=mT2;
                pdf.setFontSize(14);pdf.setFont('helvetica','bold');pdf.setTextColor(28,28,28);
                {const tl2=pdf.splitTextToSize(drawLabel,A4W2-mL2-mR2);pdf.text(tl2,mL2,cy2);cy2+=tl2.length*16+2;}
                pdf.setFontSize(9);pdf.setFont('helvetica','normal');pdf.setTextColor(89,97,99);
                pdf.text(ADDRESS+'  ·  Laikotarpis: '+dateFrom+' - '+dateTo,mL2,cy2);cy2+=16;
                const drGroupMap={};
                const drwSc2=getDrawingScale(drawing);
                drFiltered.forEach(m=>{
                  const tmpl=templates.find(t=>t.id===m.templateId);if(!tmpl)return;
                  const rawVal=computeMarkingValue(m);
                  const isLinear=tmpl.shape==='line'||tmpl.shape==='polyline';
                  (tmpl.positions||[]).forEach(pos=>{
                    const gId=String(pos.groupId||'kita'),gName=pos.groupName||'Kita';
                    const key=String(pos.itemId||pos.name);
                    if(!drGroupMap[gId])drGroupMap[gId]={groupName:gName,items:{}};
                    if(!drGroupMap[gId].items[key])drGroupMap[gId].items[key]={pos:{...pos,tmplName:tmpl.name},total:0};
                    let qty=0;
                    if(pos.unit==='vnt.'||!isLinear){qty=pos.qtyPerUnit*1;}
                    else{qty=(drwSc2?rawVal*drwSc2:rawVal)*pos.qtyPerUnit;}
                    drGroupMap[gId].items[key].total+=qty;
                  });
                });
                Object.entries(drGroupMap).forEach(([gId,grp])=>{
                  const zi=allZinItems.filter(it=>String(it.groupId)===gId);
                  Object.values(grp.items).forEach(e=>{const ix=zi.findIndex(it=>String(it.id)===String(e.pos.itemId));e.zinNr=ix>=0?ix+1:null;});
                });
                const drGroups=Object.values(drGroupMap);
                const drTotal=drGroups.reduce((a,g)=>a+Object.values(g.items).length,0);
                const tW2=A4W2-mL2-mR2;
                const cW2=[22,tW2-22-42-55,42,55];
                const cX2=[mL2];cW2.forEach((w,i)=>cX2.push(cX2[i]+w));
                const hdrs2=['Nr.','Pozicija (ziniarastis)','Mato vnt.','Kiekis'];
                if(drTotal===0){pdf.setFontSize(9);pdf.setTextColor(150,150,150);pdf.text('Nera suzymejumu.',mL2,cy2);}
                else {
                  drGroups.forEach(grp=>{
                    const items=Object.values(grp.items);
                    if(cy2+cGHT(grp,false,tW2)+rHT>A4H2-mB2){pdf.addPage('a4','portrait');cy2=mT2;}
                    cy2=dGHT(grp,cy2,false,tW2,cX2,cW2,hdrs2);
                    pdf.setFont('helvetica','normal');
                    items.forEach((row,idx)=>{
                      const nm=lt(row.pos.name||'');
                      const rh=cRowHT(nm);
                      if(cy2+rh>A4H2-mB2){pdf.addPage('a4','portrait');cy2=mT2;cy2=dGHT(grp,cy2,true,tW2,cX2,cW2,hdrs2);pdf.setFont('helvetica','normal');}
                      const bg=idx%2===0?[248,249,251]:[255,255,255];
                      pdf.setFillColor(...bg);pdf.rect(mL2,cy2,tW2,rh,'F');
                      pdf.setDrawColor(220,220,220);pdf.rect(mL2,cy2,tW2,rh,'S');
                      pdf.setTextColor(28,28,28);pdf.setFontSize(fsT);
                      const qStr=Number.isInteger(row.total)?String(row.total):parseFloat(row.total.toFixed(3)).toString();
                      const nr=row.zinNr!=null?String(row.zinNr):String(idx+1);
                      const mY2=cy2+rh/2+fsT/2-1;
                      [[0,nr],[2,row.pos.unit||''],[3,qStr]].forEach(([i,cell])=>{
                        pdf.text(String(cell),i>=2?cX2[i]+cW2[i]-3:cX2[i]+3,mY2,{align:i>=2?'right':'left'});
                      });
                      const nls=pdf.splitTextToSize(nm,cW2[1]-6);
                      const tbH=nls.length*lsT;
                      const tsY=cy2+(rh-tbH)/2+lsT*0.75;
                      nls.forEach((l,li)=>pdf.text(l,cX2[1]+3,tsY+li*lsT));
                      cy2+=rh;
                    });
                    cy2+=5;
                  });
                  if(cy2+14>A4H2-mB2){pdf.addPage('a4','portrait');cy2=mT2;}
                  pdf.setFont('helvetica','bold');pdf.setFontSize(8);pdf.setTextColor(89,97,99);
                  pdf.text('Is viso poziciju: '+drTotal,mL2,cy2+4);
                }
              }

              // ── Pagalbinė funkcija: A3 brėžinys ──
              const renderDrawingPage = async (drawing) => {
                const drawLabel = lt(getLabel(drawing));
                const drFiltered = markings.filter(m=>m.drawingId===drawing.id&&m.createdAt>=dateFrom&&m.createdAt<=dateTo);
                if (!isFirstPage) pdf.addPage('a3','landscape');
                isFirstPage = false;
                const A3W=1190,A3H=841;
                const buf = idbPdfs[drawing.id];
                if (buf) {
                  try {
                    const pdfDoc2=await lib.getDocument({data:buf.slice(0)}).promise;
                    const pg2=await pdfDoc2.getPage(drawing.pageNum || 1);
                    const vpN=pg2.getViewport({scale:1});
                    const RENDER_SCALE3 = 3;
                    const fs3=Math.min(A3W/vpN.width,A3H/vpN.height)*RENDER_SCALE3;
                    const vp3=pg2.getViewport({scale:fs3});
                    const oc=document.createElement('canvas');
                    oc.width=Math.ceil(vp3.width);oc.height=Math.ceil(vp3.height);
                    const ctx3=oc.getContext('2d');
                    ctx3.fillStyle='#fff';ctx3.fillRect(0,0,oc.width,oc.height);
                    await pg2.render({canvasContext:ctx3,viewport:vp3}).promise;
                    const DPR3=window.devicePixelRatio||1;
                    const c2c=fs3/(2/DPR3);
                    drFiltered.forEach(m=>{
                      const tmpl=templates.find(t=>t.id===m.templateId);if(!tmpl||!m.points||!m.points.length)return;
                      const col=tmpl.color||'#735DFF',sw=(tmpl.strokeWidth||2)*c2c,sz=(tmpl.size||10)*c2c;
                      ctx3.save();ctx3.strokeStyle=col;ctx3.fillStyle=col;ctx3.lineWidth=sw;ctx3.lineCap='round';ctx3.lineJoin='round';
                      const px=p=>p.x*c2c,py=p=>p.y*c2c;
                      if(m.shape==='line'&&m.points.length>=2){ctx3.beginPath();ctx3.moveTo(px(m.points[0]),py(m.points[0]));ctx3.lineTo(px(m.points[m.points.length-1]),py(m.points[m.points.length-1]));ctx3.stroke();}
                      else if(m.shape==='polyline'&&m.points.length>=2){ctx3.beginPath();ctx3.moveTo(px(m.points[0]),py(m.points[0]));m.points.slice(1).forEach(p=>ctx3.lineTo(px(p),py(p)));ctx3.stroke();}
                      else if(m.points.length>0){
                        const cx4=px(m.points[0]),cy4=py(m.points[0]);
                        const pp2=(n,r,off=0)=>{ctx3.beginPath();for(let i=0;i<n;i++){const a=2*Math.PI/n*i+off-Math.PI/2;i===0?ctx3.moveTo(cx4+Math.cos(a)*r,cy4+Math.sin(a)*r):ctx3.lineTo(cx4+Math.cos(a)*r,cy4+Math.sin(a)*r);}ctx3.closePath();};
                        const sp2=(n,r1,r2)=>{ctx3.beginPath();for(let i=0;i<n*2;i++){const a=Math.PI/n*i-Math.PI/2;const r=i%2===0?r1:r2;i===0?ctx3.moveTo(cx4+Math.cos(a)*r,cy4+Math.sin(a)*r):ctx3.lineTo(cx4+Math.cos(a)*r,cy4+Math.sin(a)*r);}ctx3.closePath();};
                        if(m.shape==='circle'){ctx3.beginPath();ctx3.arc(cx4,cy4,sz,0,Math.PI*2);ctx3.fill();}
                        else if(m.shape==='rect'){ctx3.fillRect(cx4-sz/2,cy4-sz/2,sz,sz);}
                        else if(m.shape==='triangle'){ctx3.beginPath();ctx3.moveTo(cx4,cy4-sz);ctx3.lineTo(cx4-sz*0.866,cy4+sz*0.5);ctx3.lineTo(cx4+sz*0.866,cy4+sz*0.5);ctx3.closePath();ctx3.fill();}
                        else if(m.shape==='triangle_down'){ctx3.beginPath();ctx3.moveTo(cx4,cy4+sz);ctx3.lineTo(cx4-sz*0.866,cy4-sz*0.5);ctx3.lineTo(cx4+sz*0.866,cy4-sz*0.5);ctx3.closePath();ctx3.fill();}
                        else if(m.shape==='diamond'){ctx3.beginPath();ctx3.moveTo(cx4,cy4-sz);ctx3.lineTo(cx4+sz*0.75,cy4);ctx3.lineTo(cx4,cy4+sz);ctx3.lineTo(cx4-sz*0.75,cy4);ctx3.closePath();ctx3.fill();}
                        else if(m.shape==='star'){sp2(5,sz,sz*0.45);ctx3.fill();}
                        else if(m.shape==='star6'){sp2(6,sz,sz*0.5);ctx3.fill();}
                        else if(m.shape==='cross'){ctx3.fillRect(cx4-sz/4,cy4-sz,sz/2,sz*2);ctx3.fillRect(cx4-sz,cy4-sz/4,sz*2,sz/2);}
                        else if(m.shape==='pentagon'){pp2(5,sz);ctx3.fill();}
                        else if(m.shape==='hexagon'){pp2(6,sz);ctx3.fill();}
                        else if(m.shape==='octagon'){pp2(8,sz);ctx3.fill();}
                        else if(m.shape==='arrow_right'){ctx3.beginPath();ctx3.moveTo(cx4-sz,cy4-sz*0.35);ctx3.lineTo(cx4+sz*0.3,cy4-sz*0.35);ctx3.lineTo(cx4+sz*0.3,cy4-sz*0.7);ctx3.lineTo(cx4+sz,cy4);ctx3.lineTo(cx4+sz*0.3,cy4+sz*0.7);ctx3.lineTo(cx4+sz*0.3,cy4+sz*0.35);ctx3.lineTo(cx4-sz,cy4+sz*0.35);ctx3.closePath();ctx3.fill();}
                        else if(m.shape==='arrow_left'){ctx3.beginPath();ctx3.moveTo(cx4+sz,cy4-sz*0.35);ctx3.lineTo(cx4-sz*0.3,cy4-sz*0.35);ctx3.lineTo(cx4-sz*0.3,cy4-sz*0.7);ctx3.lineTo(cx4-sz,cy4);ctx3.lineTo(cx4-sz*0.3,cy4+sz*0.7);ctx3.lineTo(cx4-sz*0.3,cy4+sz*0.35);ctx3.lineTo(cx4+sz,cy4+sz*0.35);ctx3.closePath();ctx3.fill();}
                        else if(m.shape==='arrow_both'){ctx3.beginPath();ctx3.moveTo(cx4-sz,cy4);ctx3.lineTo(cx4-sz*0.4,cy4-sz*0.6);ctx3.lineTo(cx4-sz*0.4,cy4-sz*0.3);ctx3.lineTo(cx4+sz*0.4,cy4-sz*0.3);ctx3.lineTo(cx4+sz*0.4,cy4-sz*0.6);ctx3.lineTo(cx4+sz,cy4);ctx3.lineTo(cx4+sz*0.4,cy4+sz*0.6);ctx3.lineTo(cx4+sz*0.4,cy4+sz*0.3);ctx3.lineTo(cx4-sz*0.4,cy4+sz*0.3);ctx3.lineTo(cx4-sz*0.4,cy4+sz*0.6);ctx3.closePath();ctx3.fill();}
                        else if(m.shape==='circle_cross'){ctx3.beginPath();ctx3.arc(cx4,cy4,sz,0,Math.PI*2);ctx3.stroke();ctx3.beginPath();ctx3.moveTo(cx4-sz,cy4);ctx3.lineTo(cx4+sz,cy4);ctx3.stroke();ctx3.beginPath();ctx3.moveTo(cx4,cy4-sz);ctx3.lineTo(cx4,cy4+sz);ctx3.stroke();}
                        else if(m.shape==='rect_cross'){ctx3.strokeRect(cx4-sz,cy4-sz,sz*2,sz*2);ctx3.beginPath();ctx3.moveTo(cx4-sz,cy4-sz);ctx3.lineTo(cx4+sz,cy4+sz);ctx3.stroke();ctx3.beginPath();ctx3.moveTo(cx4+sz,cy4-sz);ctx3.lineTo(cx4-sz,cy4+sz);ctx3.stroke();}
                        else if(m.shape==='double_circle'){ctx3.beginPath();ctx3.arc(cx4,cy4,sz,0,Math.PI*2);ctx3.stroke();ctx3.beginPath();ctx3.arc(cx4,cy4,sz*0.55,0,Math.PI*2);ctx3.fill();}
                        else if(m.shape==='ellipse'){ctx3.beginPath();ctx3.ellipse(cx4,cy4,sz*1.5,sz*0.7,0,0,Math.PI*2);ctx3.fill();}
                      }
                      ctx3.restore();
                    });
                    const imgD=oc.toDataURL('image/jpeg',0.95);
                    const asp=oc.width/oc.height;
                    let iW=A3W,iH=A3W/asp;if(iH>A3H-22){iH=A3H-22;iW=iH*asp;}
                    pdf.setFontSize(9);pdf.setTextColor(89,97,99);
                    pdf.text(drawLabel+'  ·  '+dateFrom+' - '+dateTo,20,16,{maxWidth:A3W-40});
                    pdf.setTextColor(0,0,0);
                    pdf.addImage(imgD,'JPEG',(A3W-iW)/2,22,iW,iH);
                  } catch(ex){
                    pdf.setFontSize(12);pdf.setTextColor(150,150,150);
                    pdf.text('Brezinys neprisikrove: '+drawLabel,A3W/2,A3H/2,{align:'center'});
                  }
                } else {
                  pdf.setFontSize(12);pdf.setTextColor(150,150,150);
                  pdf.text('Brezinys neikrautas: '+drawLabel,A3W/2,A3H/2,{align:'center'});
                }
              }

              // ── Lapų eiliškumas pagal pasirinktą struktūrą ──
              if (lib) {
                if (pdfStructure === 'paired') {
                  // PORINĖ: Br.1 → Lent.1 → Br.2 → Lent.2 → Bendra
                  for (const drawing of selDrawings) {
                    await renderDrawingPage(drawing);
                    renderDrawingTable(drawing);
                  }
                } else {
                  // SUGRUPUOTA: Br.1 → Br.2 → Lent.1 → Lent.2 → Bendra
                  for (const drawing of selDrawings) {
                    await renderDrawingPage(drawing);
                  }
                  for (const drawing of selDrawings) {
                    renderDrawingTable(drawing);
                  }
                }
              } // end if lib

              // ── 2 BLOKAS: Bendra suvestinė (A4 landscape) ──
              if (!isFirstPage) pdf.addPage('a4','landscape');
              isFirstPage = false;
              let curY=mT;
              pdf.setFontSize(14);pdf.setFont('helvetica','bold');pdf.setTextColor(28,28,28);
              {const tls=pdf.splitTextToSize(titleLine,tableW);pdf.text(tls,mL,curY);curY+=tls.length*16+2;}
              pdf.setFontSize(8);pdf.setFont('helvetica','normal');pdf.setTextColor(89,97,99);
              pdf.text(ADDRESS+'  ·  Laikotarpis: '+dateFrom+' - '+dateTo,mL,curY);curY+=12;

              function calcRowHr(nm){pdf.setFontSize(fsSum);pdf.setFont('helvetica','normal');const l=pdf.splitTextToSize(String(nm),sumCols[1].w-4);return Math.max(hdrRowHSum,l.length*lsSum+3);}
              function calcGrpHr(grp,cont){pdf.setFontSize(8);pdf.setFont('helvetica','bold');const l=pdf.splitTextToSize(lt(grp.groupName)+(cont?' (tesinys)':''),tableW-8);return Math.max(groupHdrH,l.length*9+4);}
              function drawGrpHdr2(grp,cy,cont){
                const gh=calcGrpHr(grp,cont);
                pdf.setFillColor(204,37,0);pdf.rect(mL,cy,tableW,gh,'F');
                pdf.setTextColor(255,255,255);pdf.setFontSize(8);pdf.setFont('helvetica','bold');
                pdf.splitTextToSize(lt(grp.groupName)+(cont?' (tesinys)':''),tableW-8).forEach((l,i)=>pdf.text(l,mL+4,cy+9+i*9));
                cy+=gh;
                pdf.setFillColor(89,97,99);pdf.rect(mL,cy,tableW,hdrRowHSum,'F');
                pdf.setTextColor(255,255,255);pdf.setFontSize(fsSum);pdf.setFont('helvetica','bold');
                sumCols.forEach((col,i)=>{pdf.text(lt(col.label).substring(0,Math.floor(col.w/fsSum*1.8)),col.align==='right'?sumColX[i]+col.w-2:sumColX[i]+2,cy+hdrRowHSum-3,{align:col.align,maxWidth:col.w-2});});
                return cy+hdrRowHSum;
              }

              if(totalItems===0){pdf.setFontSize(10);pdf.setTextColor(150,150,150);pdf.text('Nera suzymejumu.',mL,curY);}
              else {
                allGroupsArr.forEach(grp=>{
                  const grpItems=Object.values(grp.items);
                  if(curY+calcGrpHr(grp,false)+hdrRowHSum>PH-mB){pdf.addPage('a4','landscape');curY=mT;}
                  curY=drawGrpHdr2(grp,curY,false);
                  pdf.setFont('helvetica','normal');
                  grpItems.forEach((row,idx)=>{
                    const nm=lt(row.pos.name||'');
                    const rh=calcRowHr(nm);
                    if(curY+rh>PH-mB){pdf.addPage('a4','landscape');curY=mT;curY=drawGrpHdr2(grp,curY,true);pdf.setFont('helvetica','normal');}
                    const bg=idx%2===0?[248,249,251]:[255,255,255];
                    pdf.setFillColor(...bg);pdf.rect(mL,curY,tableW,rh,'F');
                    pdf.setDrawColor(220,220,220);pdf.rect(mL,curY,tableW,rh,'S');
                    pdf.setTextColor(28,28,28);pdf.setFontSize(fsSum);
                    const mY=curY+rh/2+fsSum/2-1;
                    const nr=row.zinNr!=null?String(row.zinNr):String(idx+1);
                    pdf.text(nr,sumColX[0]+2,mY);
                    {const nl=pdf.splitTextToSize(nm,sumCols[1].w-4);const tbH=nl.length*lsSum;const tsY=curY+(rh-tbH)/2+lsSum*0.8;nl.forEach((l,li)=>pdf.text(l,sumColX[1]+2,tsY+li*lsSum));}
                    pdf.text(row.pos.unit||'',sumColX[2]+sumCols[2].w-2,mY,{align:'right'});
                    pdf.setFont('helvetica','bold');
                    pdf.text(fmtQty(row.total),sumColX[3]+sumCols[3].w-2,mY,{align:'right'});
                    pdf.setFont('helvetica','normal');
                    selDrawings.forEach((d,di)=>{const v=row.byDrawing[d.id]||0;if(v)pdf.text(fmtQty(v),sumColX[4+di]+sumCols[4+di].w-2,mY,{align:'right'});});
                    pdf.setDrawColor(200,200,200);
                    sumCols.forEach((_,i)=>{if(i>0)pdf.line(sumColX[i],curY,sumColX[i],curY+rh);});
                    curY+=rh;
                  });
                  curY+=4;
                });
                if(curY+14>PH-mB){pdf.addPage('a4','landscape');curY=mT;}
                pdf.setFont('helvetica','bold');pdf.setFontSize(7.5);pdf.setTextColor(89,97,99);
                pdf.text('Is viso poziciju: '+totalItems+'  |  Breziniai: '+drawingIds.length,mL,curY+4);
              }

              // ── Puslapio numeravimas ──
              const pageCount=pdf.getNumberOfPages();
              for(let i=1;i<=pageCount;i++){
                pdf.setPage(i);pdf.setFontSize(7);pdf.setTextColor(150,150,150);
                const pgW=pdf.internal.pageSize.getWidth();
                const pgH=pdf.internal.pageSize.getHeight();
                pdf.text(String(i)+' / '+String(pageCount),pgW-mR,pgH-12,{align:'right'});
                pdf.text('AMTEKA - Sugeneruota: '+new Date().toISOString().split('T')[0],mL,pgH-12);
              }

              const safeName=reportTitle&&reportTitle.trim()
                ?lt(reportTitle.trim()).replace(/[^a-zA-Z0-9 _\-]/g,'').replace(/\s+/g,'_').substring(0,60)
                :'UAB_AMTEKA_ISPILDOMOSIOS_BENDRA_SUVESTINE_'+new Date().toISOString().slice(0,7).replace('-','.');
              pdf.save(safeName+'_'+dateFrom+'_'+dateTo+'.pdf');

            } catch(err){
              console.error('Multi-PDF klaida:',err);alert('PDF generavimo klaida: '+err.message);
            } finally {
              setExporting(false);setShowMultiExport(false);
            }
          }

                    // ─── Pan / zoom ───
          function handleWheel(e) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            const rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            setZoom(z => {
              const nz = Math.max(0.05, Math.min(20, z * delta));
              setPan(p => ({ x: mouseX - (mouseX - p.x) * (nz / z), y: mouseY - (mouseY - p.y) * (nz / z) }));
              return nz;
            });
          }
          function zoomBy(factor) {
            const el = containerRef.current;
            const cx = el ? el.clientWidth / 2 : 400, cy = el ? el.clientHeight / 2 : 300;
            setZoom(z => {
              const nz = Math.max(0.05, Math.min(20, z * factor));
              setPan(p => ({ x: cx - (cx - p.x) * (nz / z), y: cy - (cy - p.y) * (nz / z) }));
              return nz;
            });
          }

          function handleMouseDown(e) {
            if (mode === 'select' || (mode === 'draw' && e.button === 2) || (mode === 'calibrate' && e.button === 2)) {
              if (e.button === 0 || e.button === 1) {
                setIsPanning(true); panMovedRef.current = false;
                setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
                e.preventDefault();
              }
            }
          }
          function handleMouseMove(e) {
            const rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left:0, top:0 };
            const cp = screenToContent(e.clientX - rect.left, e.clientY - rect.top);
            setMousePos(cp);
            if (isPanning) { panMovedRef.current = true; setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y }); }
          }
          function handleMouseUp() { setIsPanning(false); }

          // drawingPoints: [{ x, y }, ...] — kaupiami taškai aktyviam žymėjimui
          // lineStartPoint: paskutinis užfiksuotas taškas (linijoms — iš čia pradedama nauja atkarpa)
          const lineStartRef = useRef(null); // { x, y } arba null

          function handleCanvasClick(e) {
            if (panMovedRef.current) { panMovedRef.current = false; return; }
            const rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left:0, top:0 };
            const cp = screenToContent(e.clientX - rect.left, e.clientY - rect.top);

            // Patalpos kontūro žymėjimas — kiekvienas taškas veikia kaip "pin",
            // prie kurio artimas paspaudimas prisitraukia; paspaudus arti
            // pradinio taško (ir turint bent 3 taškus), kontūras užsidaro ir
            // patalpa suformuojama automatiškai (Enter nebereikalingas).
            if (roomMode) {
              const snapDist = ROOM_SNAP_PX / zoom;
              if (roomPoints.length >= 3) {
                const start = roomPoints[0];
                if (Math.hypot(cp.x - start.x, cp.y - start.y) <= snapDist) {
                  finishRoom(roomPoints);
                  return;
                }
              }
              const pin = roomPoints.find(p => Math.hypot(cp.x - p.x, cp.y - p.y) <= snapDist);
              setRoomPoints(prev => [...prev, pin || cp]);
              return;
            }

            // Grindinio zonos kontūro žymėjimas
            if (floorZoneMode) {
              setFloorZonePoints(prev => [...prev, cp]);
              return;
            }

            if (mode === 'calibrate') {
              const newPts = [...calibPoints, cp];
              setCalibPoints(newPts);
              if (newPts.length === 2) setShowCalibInput(true);
              return;
            }

            if (mode === 'draw' && activeTemplate) {
              const tmpl = activeTemplate;

              if (tmpl.shape === 'circle' || tmpl.shape === 'rect' || tmpl.shape === 'triangle' || tmpl.shape === 'triangle_down' || tmpl.shape === 'diamond' || tmpl.shape === 'star' || tmpl.shape === 'star6' || tmpl.shape === 'cross' || tmpl.shape === 'pentagon' || tmpl.shape === 'hexagon' || tmpl.shape === 'octagon' || tmpl.shape === 'arrow_right' || tmpl.shape === 'arrow_left' || tmpl.shape === 'arrow_both' || tmpl.shape === 'circle_cross' || tmpl.shape === 'rect_cross' || tmpl.shape === 'double_circle' || tmpl.shape === 'ellipse') {
                // Taškiniai elementai — vienas paspaudimas = vienas žymėjimas iš karto
                finishDrawing([cp]);
                return;
              }

              if (tmpl.shape === 'line') {
                // Linija: kiekvienas paspaudimas užbaigia atkarpą nuo pradžios taško
                if (lineStartRef.current === null) {
                  // Pirmasis taškas — tik nustatome pradžią
                  lineStartRef.current = cp;
                  setDrawingPoints([cp]);
                } else {
                  // Antrasis taškas — išsaugome atkarpą ir pradedame naują nuo šio taško
                  finishDrawing([lineStartRef.current, cp]);
                  lineStartRef.current = cp; // nauja atkarpa pradedama nuo čia
                  setDrawingPoints([cp]);
                }
                return;
              }

              if (tmpl.shape === 'polyline') {
                // Polilinija: kaupiame taškus, Enter arba 2× paspaudimas baigia
                setDrawingPoints(prev => [...prev, cp]);
                return;
              }
            }
          }

          const [showKonfigurator, setShowKonfigurator] = useState(false);

          function handleCanvasDblClick(e) {
            e.preventDefault();
            // Jei piešiame polyline — baigti piešimą
            if (mode === 'draw' && activeTemplate && activeTemplate.shape === 'polyline' && drawingPoints.length >= 2) {
              finishDrawing(drawingPoints.slice(0, -1));
              return;
            }
            // Kitais atvejais — atidaryti Konfigūratorių
            if (mode === 'select' || (!activeTemplate && mode === 'draw')) {
              setShowKonfigurator(true);
            }
          }

          // Klaviatūros valdymas
          useEffect(() => {
            function onKey(e) {
              // Ignoruoti kai fokusas yra input/textarea laukelyje
              const tag = document.activeElement && document.activeElement.tagName;
              if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

              // Delete — ištrinti pasirinktą žymėjimą
              if ((e.key === 'Delete' || e.key === 'Backspace') && selectedMarkingId) {
                e.preventDefault();
                const selM = markings.find(m => m.id === selectedMarkingId);
                setMarkings(prev => prev.filter(m => m.id !== selectedMarkingId));
                setSelectedMarkingId(null);
                return;
              }

              // Enter — užfiksuoti piešimą
              if (e.key === 'Enter' && mode === 'draw' && activeTemplate) {
                e.preventDefault();
                if (activeTemplate.shape === 'line') {
                  lineStartRef.current = null;
                  setDrawingPoints([]);
                } else if (activeTemplate.shape === 'polyline' && drawingPoints.length >= 2) {
                  finishDrawing(drawingPoints);
                }
                return;
              }

              // Enter — užfiksuoti patalpos kontūrą
              if (e.key === 'Enter' && roomMode && roomPoints.length >= 3) {
                e.preventDefault();
                finishRoom(roomPoints);
                return;
              }

              // Enter — užfiksuoti grindinio zonos kontūrą
              if (e.key === 'Enter' && floorZoneMode && floorZonePoints.length >= 3) {
                e.preventDefault();
                finishFloorZone(floorZonePoints);
                return;
              }

              // Escape — pirmas: uždaryti Konfigūratorių
              if (e.key === 'Escape') {
                // Jei atidaryta šablono redagavimo kortelė — leisti jos handleriui valdyti
                if (showTemplateEditor) return;
                e.preventDefault();
                if (showKonfigurator) { setShowKonfigurator(false); return; }
                // Patalpos kontūras
                if (roomMode) {
                  setRoomPoints([]);
                  setRoomMode(false);
                  setPendingRoomName('');
                  setShowRoomNameInput(false);
                  return;
                }
                // Grindinio zonos kontūras
                if (floorZoneMode) {
                  setFloorZonePoints([]);
                  setFloorZoneMode(false);
                  setFloorZoneLayerId(null);
                  return;
                }
                const isDrawingActive = mode === 'draw' && (lineStartRef.current !== null || drawingPoints.length > 0);
                if (isDrawingActive) {
                  lineStartRef.current = null;
                  setDrawingPoints([]);
                } else {
                  setMode('select');
                  setActiveTemplateId(null);
                  lineStartRef.current = null;
                  setDrawingPoints([]);
                }
                return;
              }
            }
            window.addEventListener('keydown', onKey);
            return () => window.removeEventListener('keydown', onKey);
          }, [mode, activeTemplate, drawingPoints, selectedMarkingId, roomMode, roomPoints, showKonfigurator, floorZoneMode, floorZonePoints, showTemplateEditor]);

          function finishDrawing(points) {
            if (!activeTemplate || !selectedDrawing) return;
            const today = new Date(); const dateStr = today.getFullYear() + '-' + String(today.getMonth()+1).padStart(2,'0') + '-' + String(today.getDate()).padStart(2,'0');
            const marking = {
              id: Date.now(),
              templateId: activeTemplate.id,
              drawingId: selectedDrawing.id,
              points,
              shape: activeTemplate.shape,
              createdAt: dateStr,
              layerId: activeLayerId || null,
            };
            setMarkings(prev => [...prev, marking]);
            setDrawingPoints([]);
          }

          function cancelDrawing() {
            lineStartRef.current = null;
            setDrawingPoints([]);
          }

          // ─── PDF įkėlimas ───
          // Faktinis brėžinio(-ių) sukūrimas: vienas puslapis arba, patvirtinus, visi failo puslapiai.
          async function importPdfPages(file, buf, baseName, numPages) {
            const baseId = Date.now();
            const createdAt = new Date().toISOString().split('T')[0];
            const newDrawings = [];
            const newIdbPdfs = {};

            for (let p = 1; p <= numPages; p++) {
              const id = baseId + p; // unikalus id kiekvienam lapui
              const name = numPages > 1 ? `${baseName} — ${p} lapas` : baseName;
              const drawing = { id, name, createdAt, sourceFile: baseName, pageNum: p, totalPages: numPages };
              await TAKEOFF_DB.save(id, buf); // saugome visą buferį; pageNum naudojame renderinant
              newDrawings.push(drawing);
              newIdbPdfs[id] = buf;
              loadedPdfIds.current.add(id);
            }

            setDrawings(prev => [...prev, ...newDrawings]);
            setIdbPdfs(prev => ({ ...prev, ...newIdbPdfs }));
            selectDrawing(newDrawings[0].id);

            // Sinchronizuojame su serveriu (durable source of truth). Visi
            // šio failo lapai (newDrawings) dalinasi tais pačiais baitais,
            // todėl užtenka vieno upload'o — grąžintą serverId pažymime
            // visiems to failo brėžinio-lapo įrašams. Nepavykus — netrikdome
            // lokalaus flow, brėžinys tiesiog neprieinamas iš kito naršyklės.
            const pageIds = newDrawings.map(d => d.id);
            drawingsApi.upload(activeProjectId, file).then(meta => {
              if (!meta || !meta.id) return;
              setDrawings(prev => prev.map(d => pageIds.includes(d.id) ? { ...d, serverId: meta.id } : d));
            }).catch(err => console.warn('Failed to sync drawing to server', err));
          }

          async function handlePdfUpload(e) {
            const file = e.target.files[0]; if (!file) return;
            e.target.value = ''; // jau turime File objektą, galima iškart leisti pasirinkti tą patį failą iš naujo

            setPdfDetecting(true);
            try {
              const buf = await file.arrayBuffer();
              const baseName = file.name.replace(/\.pdf$/i, '');

              const lib = pdfjsLib;
              if (!lib) { alert('PDF.js biblioteka nepasiekiama'); return; }

              let numPages = 1;
              try {
                const doc = await lib.getDocument({ data: buf.slice(0) }).promise;
                numPages = doc.numPages;
              } catch(err) {
                console.error('PDF count error:', err);
                alert('Nepavyko nuskaityti PDF failo. Failas gali būti sugadintas arba netinkamo formato.');
                return;
              }

              if (numPages > 1) {
                // Kelių puslapių PDF — klausiame vartotojo prieš skaidydami į atskirus brėžinius
                setPdfPendingSplit({ file, buf, baseName, numPages });
              } else {
                await importPdfPages(file, buf, baseName, 1);
              }
            } finally {
              setPdfDetecting(false);
            }
          }

          // ─── Šablonas: spalvų paletė ───
          const PALETTE = ['#735DFF','#1e88e5','#43a047','#f4511e','#8e24aa','#00897b','#fb8c00','#3949ab','#d81b60','#546e7a','#000000','#ffffff'];

          // ─── SVG piešimas ───
          function renderMarkingSvg(m, tmpl, isSelected) {
            if (!tmpl) return null;
            // Sluoksnio spalva ir matomumas
            if (m.layerId && !isLayerVisible(m.layerId)) return null;
            const layer = m.layerId ? layers.find(l => l.id === m.layerId) : null;
            const col = isSelected ? '#FFD700' : (layer ? layer.color : (tmpl.color || '#735DFF'));
            const opacity = 1;
            // vector-effect="non-scaling-stroke" — strokeWidth visada ekrano pikseliais,
            // nepriklausomai nuo zoom. Todėl čia NEREIKIA dalinti iš zoom.
            const strokeW = isSelected ? (tmpl.strokeWidth || 2) * 2.5 : (tmpl.strokeWidth || 2);
            const pts = m.points;
            if (!pts || pts.length === 0) return null;

            const clickHandler = e => { e.stopPropagation(); setSelectedMarkingId(m.id === selectedMarkingId ? null : m.id); };
            // Taškinių formų dydis — fiksuotas ekrano pikseliais (/ zoom konvertuoja į brėžinio erdvę)
            const screenSz = tmpl.size || 12;
            const sz = screenSz / zoom;

            if (tmpl.shape === 'line') {
              if (pts.length < 2) return null;
              return <line key={m.id} x1={pts[0].x} y1={pts[0].y} x2={pts[pts.length-1].x} y2={pts[pts.length-1].y}
                stroke={col} strokeWidth={strokeW} strokeLinecap="round" opacity={opacity}
                vectorEffect="non-scaling-stroke"
                onClick={clickHandler} style={{cursor:'pointer'}} />;
            }
            if (tmpl.shape === 'polyline') {
              const d = pts.map((p,i)=>(i===0?`M${p.x},${p.y}`:`L${p.x},${p.y}`)).join(' ');
              return <path key={m.id} d={d} stroke={col} strokeWidth={strokeW} fill="none" strokeLinecap="round" strokeLinejoin="round" opacity={opacity}
                vectorEffect="non-scaling-stroke"
                onClick={clickHandler} style={{cursor:'pointer'}} />;
            }
            // Taškinės formos — visos naudoja pts[0] kaip centrą
            const cx = pts[0].x, cy = pts[0].y;
            // Pagalbinės funkcijos
            const polyPts = (n, r, offAng=0) => Array.from({length:n},(_,i)=>{const a=2*Math.PI/n*i+offAng-Math.PI/2;return `${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}).join(' ');
            const star5Pts = (r1,r2) => Array.from({length:10},(_,i)=>{const a=Math.PI/5*i-Math.PI/2;const r=i%2===0?r1:r2;return `${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}).join(' ');
            const star6Pts = (r1,r2) => Array.from({length:12},(_,i)=>{const a=Math.PI/6*i-Math.PI/2;const r=i%2===0?r1:r2;return `${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}).join(' ');
            const commonFill = {fill:col, stroke:"#fff", strokeWidth:1/zoom, opacity, onClick:clickHandler, style:{cursor:'pointer'}};
            const commonStroke = {fill:"none", stroke:col, strokeWidth:strokeW, opacity, onClick:clickHandler, style:{cursor:'pointer'}, vectorEffect:"non-scaling-stroke"};
            if (tmpl.shape === 'circle') return <circle key={m.id} cx={cx} cy={cy} r={sz} {...commonFill}/>;
            if (tmpl.shape === 'rect') return <rect key={m.id} x={cx-sz/2} y={cy-sz/2} width={sz} height={sz} {...commonFill}/>;
            if (tmpl.shape === 'triangle') return <polygon key={m.id} points={`${cx},${cy-sz} ${cx-sz*0.866},${cy+sz*0.5} ${cx+sz*0.866},${cy+sz*0.5}`} {...commonFill}/>;
            if (tmpl.shape === 'triangle_down') return <polygon key={m.id} points={`${cx},${cy+sz} ${cx-sz*0.866},${cy-sz*0.5} ${cx+sz*0.866},${cy-sz*0.5}`} {...commonFill}/>;
            if (tmpl.shape === 'diamond') return <polygon key={m.id} points={`${cx},${cy-sz} ${cx+sz*0.75},${cy} ${cx},${cy+sz} ${cx-sz*0.75},${cy}`} {...commonFill}/>;
            if (tmpl.shape === 'star') return <polygon key={m.id} points={star5Pts(sz, sz*0.45)} {...commonFill}/>;
            if (tmpl.shape === 'star6') return <polygon key={m.id} points={star6Pts(sz, sz*0.5)} {...commonFill}/>;
            if (tmpl.shape === 'cross') return <g key={m.id} onClick={clickHandler} style={{cursor:'pointer'}} opacity={opacity}>
              <rect x={cx-sz/4} y={cy-sz} width={sz/2} height={sz*2} fill={col}/>
              <rect x={cx-sz} y={cy-sz/4} width={sz*2} height={sz/2} fill={col}/>
            </g>;
            if (tmpl.shape === 'pentagon') return <polygon key={m.id} points={polyPts(5,sz)} {...commonFill}/>;
            if (tmpl.shape === 'hexagon') return <polygon key={m.id} points={polyPts(6,sz)} {...commonFill}/>;
            if (tmpl.shape === 'octagon') return <polygon key={m.id} points={polyPts(8,sz)} {...commonFill}/>;
            if (tmpl.shape === 'arrow_right') return <polygon key={m.id} points={`${cx-sz},${cy-sz*0.35} ${cx+sz*0.3},${cy-sz*0.35} ${cx+sz*0.3},${cy-sz*0.7} ${cx+sz},${cy} ${cx+sz*0.3},${cy+sz*0.7} ${cx+sz*0.3},${cy+sz*0.35} ${cx-sz},${cy+sz*0.35}`} {...commonFill}/>;
            if (tmpl.shape === 'arrow_left') return <polygon key={m.id} points={`${cx+sz},${cy-sz*0.35} ${cx-sz*0.3},${cy-sz*0.35} ${cx-sz*0.3},${cy-sz*0.7} ${cx-sz},${cy} ${cx-sz*0.3},${cy+sz*0.7} ${cx-sz*0.3},${cy+sz*0.35} ${cx+sz},${cy+sz*0.35}`} {...commonFill}/>;
            if (tmpl.shape === 'arrow_both') return <polygon key={m.id} points={`${cx-sz},${cy} ${cx-sz*0.4},${cy-sz*0.6} ${cx-sz*0.4},${cy-sz*0.3} ${cx+sz*0.4},${cy-sz*0.3} ${cx+sz*0.4},${cy-sz*0.6} ${cx+sz},${cy} ${cx+sz*0.4},${cy+sz*0.6} ${cx+sz*0.4},${cy+sz*0.3} ${cx-sz*0.4},${cy+sz*0.3} ${cx-sz*0.4},${cy+sz*0.6}`} {...commonFill}/>;
            if (tmpl.shape === 'circle_cross') return <g key={m.id} onClick={clickHandler} style={{cursor:'pointer'}} opacity={opacity}>
              <circle cx={cx} cy={cy} r={sz} {...commonStroke}/>
              <line x1={cx-sz} y1={cy} x2={cx+sz} y2={cy} stroke={col} strokeWidth={strokeW} vectorEffect="non-scaling-stroke"/>
              <line x1={cx} y1={cy-sz} x2={cx} y2={cy+sz} stroke={col} strokeWidth={strokeW} vectorEffect="non-scaling-stroke"/>
            </g>;
            if (tmpl.shape === 'rect_cross') return <g key={m.id} onClick={clickHandler} style={{cursor:'pointer'}} opacity={opacity}>
              <rect x={cx-sz} y={cy-sz} width={sz*2} height={sz*2} {...commonStroke}/>
              <line x1={cx-sz} y1={cy-sz} x2={cx+sz} y2={cy+sz} stroke={col} strokeWidth={strokeW} vectorEffect="non-scaling-stroke"/>
              <line x1={cx+sz} y1={cy-sz} x2={cx-sz} y2={cy+sz} stroke={col} strokeWidth={strokeW} vectorEffect="non-scaling-stroke"/>
            </g>;
            if (tmpl.shape === 'double_circle') return <g key={m.id} onClick={clickHandler} style={{cursor:'pointer'}} opacity={opacity}>
              <circle cx={cx} cy={cy} r={sz} {...commonStroke}/>
              <circle cx={cx} cy={cy} r={sz*0.55} fill={col}/>
            </g>;
            if (tmpl.shape === 'ellipse') return <ellipse key={m.id} cx={cx} cy={cy} rx={sz*1.5} ry={sz*0.7} {...commonFill}/>;
          }

          function renderPreviewSvg() {
            if (!activeTemplate || mode !== 'draw') return null;
            const col = activeTemplate.color || '#735DFF';
            const strokeW = activeTemplate.strokeWidth || 2;

            if (activeTemplate.shape === 'line') {
              const start = lineStartRef.current;
              if (!start || !mousePos) return null;
              return <line x1={start.x} y1={start.y} x2={mousePos.x} y2={mousePos.y}
                stroke={col} strokeWidth={strokeW} fill="none" strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                strokeDasharray={`${6/zoom},${4/zoom}`} opacity={0.7} pointerEvents="none" />;
            }

            if (activeTemplate.shape === 'polyline') {
              if (drawingPoints.length === 0) return null;
              const all = mousePos ? [...drawingPoints, mousePos] : drawingPoints;
              const d = all.map((p,i)=>(i===0?`M${p.x},${p.y}`:`L${p.x},${p.y}`)).join(' ');
              return <path d={d} stroke={col} strokeWidth={strokeW} fill="none" strokeLinecap="round" strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                strokeDasharray={`${6/zoom},${4/zoom}`} opacity={0.7} pointerEvents="none" />;
            }
            return null;
          }

          // ─── Sužymėjimų suvestinė ───
          const scl = getScale();
          const drawingMarkings = selectedDrawing ? markings.filter(m => m.drawingId === selectedDrawing.id) : [];
          const allMarkings = markings;

          // ─── Render ───
          return (
            <div style={{display:'flex',height:'calc(100vh - 120px)',background:'#f8f9fb',overflow:'hidden'}}>
              {/* Hidden file input for PDF upload */}
              <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" style={{display:'none'}} onChange={handlePdfUpload} />

              {/* ═══ KAIRĖ: Šablonai ═══ */}
              <div style={{width:templatesOpen?200:31,minWidth:templatesOpen?200:31,background:'#fff',borderRight:'1px solid #e5e7eb',display:'flex',flexDirection:'column',overflow:'hidden',transition:'width .2s,min-width .2s',position:'relative',flexShrink:0}}>

                {/* Suskleidimo mygtukas (visada matomas) */}
                {!templatesOpen && (
                  <div onClick={()=>setTemplatesOpen(true)}
                    style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',userSelect:'none',
                      margin:'8px 4px',background:'var(--ral3000-subtle)',border:'1.5px solid var(--ral3000-light)',
                      borderRadius:10,boxShadow:'0 1px 3px rgba(115,93,255,0.12)'}}
                    title="Išskleisti šablonus">
                    <span style={{fontSize:11,fontWeight:700,color:'var(--ral3000)',writingMode:'vertical-rl',textOrientation:'mixed',letterSpacing:1.2,transform:'rotate(180deg)',padding:'10px 0'}}>🔧 ŠABLONAI</span>
                  </div>
                )}

                {templatesOpen && <>
                {/* Šablonai antraštė */}
                <div style={{padding:'8px 10px 5px',borderBottom:'1px solid #f0f0f0',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                  <span style={{fontSize:11,fontWeight:800,color:'#1c1c1c',letterSpacing:0.5}}>🔧 ŠABLONAI</span>
                  <div style={{display:'flex',gap:4,alignItems:'center'}}>
                    <button onClick={()=>{setEditingTemplate(null);setShowTemplateEditor(true);}}
                      style={{background:'#735DFF',color:'#fff',border:'none',borderRadius:5,padding:'2px 7px',fontSize:11,fontWeight:700,cursor:'pointer'}}>+</button>
                    <button onClick={()=>setTemplatesOpen(false)} title="Sutraukti"
                      style={{background:'none',border:'none',cursor:'pointer',fontSize:13,color:'#9ca3af',padding:'0 2px',lineHeight:1}}>◀</button>
                  </div>
                </div>
                <div style={{flex:1,overflowY:'auto',padding:'4px 0'}}>
                  {templates.length===0 && templateGroups.length===0 && <div style={{padding:'10px 12px',fontSize:11,color:'#9ca3af',textAlign:'center'}}>Nėra šablonų.<br/>Sukurkite pirmą.</div>}
                  {(() => {
                    // Šablonų indeksas pagal grupę
                    const byGroup = {};
                    const ungrouped = [];
                    templates.forEach(tmpl => {
                      const g = (tmpl.groupName || '').trim();
                      if (g) { if (!byGroup[g]) byGroup[g] = []; byGroup[g].push(tmpl); }
                      else ungrouped.push(tmpl);
                    });
                    const hasAnyGroups = templateGroups.length > 0;
                    // Props perduodami į GroupBlock ir TemplateItem
                    const gbProps = { openTemplateGroups, setOpenTemplateGroups, activeTemplateId, mode, byGroup, ungrouped, setTemplates, setTemplateGroups };
                    const tiProps = { activeTemplateId, mode, setActiveTemplateId, setMode, lineStartRef, setDrawingPoints, setEditingTemplate, setShowTemplateEditor, setTemplates };
                    return (
                      <>
                        {templateGroups.map(g => <GroupBlock key={g} name={g} isPersistent={true} {...gbProps} templateItemProps={tiProps} />)}
                        {(() => {
                          const orphanGroups = [...new Set(
                            templates.filter(t=>(t.groupName||'').trim() && !templateGroups.includes((t.groupName||'').trim()))
                              .map(t=>(t.groupName||'').trim())
                          )];
                          return orphanGroups.map(g => <GroupBlock key={g} name={g} isPersistent={false} {...gbProps} templateItemProps={tiProps} />);
                        })()}
                        {ungrouped.length > 0 && (
                          hasAnyGroups
                            ? <GroupBlock key="__ungrouped__" name="Nepriskirta grupei" isPersistent={false} isUngrouped={true} {...gbProps} templateItemProps={tiProps} />
                            : ungrouped.map(tmpl => <TemplateItem key={tmpl.id} tmpl={tmpl} {...tiProps} />)
                        )}
                      </>
                    );
                  })()}
                </div>
                </>}
              </div>

              {/* ═══ PATALPOS: atskiras skydelis ═══ */}
              <div style={{width:roomsOpen?200:31,minWidth:roomsOpen?200:31,background:'#fff',borderLeft:'1px solid #e5e7eb',display:'flex',flexDirection:'column',overflow:'hidden',transition:'width .2s,min-width .2s',flexShrink:0}}>

                {/* Suskleidimo būsena */}
                {!roomsOpen && (
                  <div onClick={()=>setRoomsOpen(true)}
                    style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',userSelect:'none',
                      margin:'8px 4px',background:'var(--ral3000-subtle)',border:'1.5px solid var(--ral3000-light)',
                      borderRadius:10,boxShadow:'0 1px 3px rgba(115,93,255,0.12)'}}
                    title="Išskleisti patalpos">
                    <span style={{fontSize:11,fontWeight:700,color:'var(--ral3000)',writingMode:'vertical-rl',textOrientation:'mixed',letterSpacing:1.2,transform:'rotate(180deg)',padding:'10px 0'}}>🏠 PATALPOS</span>
                  </div>
                )}

                {roomsOpen && <>
                {/* Antraštė */}
                <div style={{padding:'8px 10px 5px',borderBottom:'1px solid #f0f0f0',flexShrink:0}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:4}}>
                      <span style={{fontSize:11,fontWeight:800,color:'#1c1c1c',letterSpacing:0.5}}>🏠 PATALPOS</span>
                      <button onClick={e=>{e.stopPropagation();setRoomsVisible(v=>!v);}}
                        title={roomsVisible?'Paslėpti patalpos sluoksnį':'Rodyti patalpos sluoksnį'}
                        style={{background:'none',border:'1px solid #e5e7eb',borderRadius:4,padding:'1px 4px',cursor:'pointer',fontSize:11,color:roomsVisible?'#059669':'#9ca3af',fontWeight:700}}>
                        {roomsVisible?'👁':'🙈'}
                      </button>
                    </div>
                    <div style={{display:'flex',gap:3,alignItems:'center'}}>
                      <button onClick={()=>{
                          if (roomMode) { setRoomMode(false); setRoomPoints([]); setPendingRoomName(''); setShowRoomNameInput(false); }
                          else { setShowRoomNameInput(true); setRoomPoints([]); }
                        }}
                        style={{background:roomMode?'#fef3c7':'#735DFF',color:roomMode?'#92400e':'#fff',border:'none',
                          borderRadius:5,padding:'2px 6px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                        {roomMode ? '✕' : '+ Nauja'}
                      </button>
                      <button onClick={()=>setRoomsOpen(false)} title="Sutraukti"
                        style={{background:'none',border:'none',cursor:'pointer',fontSize:13,color:'#9ca3af',padding:'0 2px',lineHeight:1}}>◀</button>
                    </div>
                  </div>
                </div>
                  {/* Lubų aukštis */}
                  <div style={{padding:'8px 10px',margin:'6px',borderRadius:10,display:'flex',flexDirection:'column',gap:6,background:'var(--ral3000)'}}>
                    <span style={{fontSize:17,color:'#fff',fontWeight:800}}>Lubų aukštis (m):</span>
                    <input type="number" step="0.01" value={roomCeilingHeight}
                      onChange={e=>setRoomCeilingHeight(e.target.value)}
                      style={{width:'100%',boxSizing:'border-box',border:'1px solid #fff',borderRadius:6,padding:'4px 8px',fontSize:18,textAlign:'left',background:'#fff',color:'var(--ral3000)',fontWeight:800}}/>
                  </div>

                {/* Naujos patalpos pavadinimas */}
                {showRoomNameInput && (
                  <div style={{padding:'6px 8px',background:'#fff8f0',borderBottom:'1px solid #fde8d0'}}>
                    <div style={{fontSize:10,fontWeight:600,color:'#92400e',marginBottom:4}}>Patalpos pavadinimas:</div>
                    <input autoFocus value={pendingRoomName} onChange={e=>setPendingRoomName(e.target.value)}
                      placeholder="pvz. Koridorius..."
                      onKeyDown={e=>{if(e.key==='Enter'){setShowRoomNameInput(false);setRoomMode(true);}if(e.key==='Escape'){setShowRoomNameInput(false);setPendingRoomName('');}}}
                      style={{width:'100%',border:'1px solid #fbd38d',borderRadius:4,padding:'4px 6px',fontSize:12,boxSizing:'border-box',marginBottom:4}}/>
                    <div style={{display:'flex',gap:4}}>
                      <button onClick={()=>{setShowRoomNameInput(false);setRoomMode(true);}}
                        style={{flex:1,background:'#735DFF',color:'#fff',border:'none',borderRadius:4,padding:'4px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                        ✓ Pradėti žymėti
                      </button>
                      <button onClick={()=>{setShowRoomNameInput(false);setPendingRoomName('');}}
                        style={{background:'#f3f4f6',border:'none',borderRadius:4,padding:'4px 6px',fontSize:11,cursor:'pointer'}}>✕</button>
                    </div>
                    <div style={{fontSize:9,color:'#9ca3af',marginTop:3}}>Enter = pradėti · Esc = atšaukti</div>
                  </div>
                )}

                {/* Aktyvaus žymėjimo statusas */}
                {roomMode && (
                  <div style={{padding:'5px 8px',background:'#fef3c7',borderBottom:'1px solid #fde68a',fontSize:10,color:'#92400e',fontWeight:600}}>
                    ✏ {pendingRoomName||'Nauja patalpa'}: {roomPoints.length} taškai
                    {roomPoints.length >= 3 && <span style={{display:'block',fontWeight:400,marginTop:1}}>Enter = užfiksuoti · Esc = atšaukti</span>}
                  </div>
                )}

                {/* Patalpos sąrašas */}
                <div style={{flex:1,overflowY:'auto',padding:'4px 0'}}>
                  {drawingRooms.length === 0 && !roomMode && (
                    <div style={{padding:'16px 10px',fontSize:11,color:'#9ca3af',textAlign:'center'}}>
                      Nėra patalpos.<br/>Sukurkite pirmą.
                    </div>
                  )}
                  {drawingRooms.map(room => {
                    const scale = getScale();
                    const h = parseFloat(roomCeilingHeight)||0;
                    const areaM2 = room.areaM2 || (scale ? calcRoomArea(room.points, scale)?.m2 : null);
                    const vol = (areaM2 && h) ? areaM2 * h : null;
                    const isHighlighted = room.id === highlightedRoomId;
                    return (
                      <div key={room.id} onClick={()=>setHighlightedRoomId(prev=>prev===room.id?null:room.id)}
                        className={isHighlighted ? '' : 'list-row-hover'}
                        style={{padding:'6px 8px',margin:'4px 6px',borderRadius:8,cursor:'pointer',
                          background:isHighlighted?'#fff8e1':'transparent',
                          border:'1px solid '+(isHighlighted?'#FFD700':'#f0f0f0'),
                          borderLeft:'3px solid '+(isHighlighted?'#FFD700':'#e5e7eb')}}>
                        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
                          <div style={{width:10,height:10,borderRadius:2,background:room.color,flexShrink:0}}/>
                          {renamingRoomId === room.id ? (
                            <input autoFocus value={renameRoomVal} onChange={e=>setRenameRoomVal(e.target.value)}
                              onClick={e=>e.stopPropagation()}
                              onKeyDown={e=>{if(e.key==='Enter')saveRoomRename(room.id);if(e.key==='Escape')setRenamingRoomId(null);}}
                              style={{flex:1,minWidth:0,border:'1px solid var(--ral3000)',borderRadius:4,padding:'2px 5px',fontSize:11,fontWeight:700,boxSizing:'border-box'}}/>
                          ) : (
                            <span style={{fontSize:11,fontWeight:700,color:'#1c1c1c',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{room.name}</span>
                          )}
                          <button onClick={(e)=>{e.stopPropagation();setRenamingRoomId(room.id);setRenameRoomVal(room.name);}}
                            style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',fontSize:11,padding:0,flexShrink:0}}>✏</button>
                          <button onClick={(e)=>{e.stopPropagation();if(window.confirm(`Ištrinti "${room.name}"?`))setRooms(prev=>prev.filter(r=>r.id!==room.id));}}
                            style={{background:'none',border:'none',color:'#d1d5db',cursor:'pointer',fontSize:14,padding:0,flexShrink:0}}>×</button>
                        </div>
                        <div style={{fontSize:10,color:'#6b7280',paddingLeft:15}}>
                          {areaM2 ? <div>📐 <strong>{areaM2.toFixed(2)}</strong> m²</div> : <div style={{color:'#f59e0b'}}>⚠ Nėra kalibravimo</div>}
                          {vol ? <div>📦 <strong>{vol.toFixed(2)}</strong> m³</div> : null}
                          {!areaM2 && <div style={{fontSize:9,color:'#9ca3af'}}>{room.points?.length||0} taškai</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Suvestinė */}
                {drawingRooms.length > 0 && (() => {
                  const scale = getScale();
                  const h = parseFloat(roomCeilingHeight)||0;
                  const totalM2 = drawingRooms.reduce((a,r) => {
                    const m2 = r.areaM2 || (scale ? calcRoomArea(r.points, scale)?.m2 : null);
                    return a + (m2||0);
                  }, 0);
                  const totalVol = h ? totalM2 * h : 0;
                  return (
                    <div style={{padding:'6px 10px',borderTop:'2px solid #e5e7eb',background:'#f8f9fb'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'#383853'}}>IŠ VISO ({drawingRooms.length} patalp.)</div>
                      <div style={{fontSize:11,fontWeight:700,color:'#1c1c1c'}}>📐 {totalM2.toFixed(2)} m²</div>
                      {h > 0 && <div style={{fontSize:11,fontWeight:700,color:'#1c1c1c'}}>📦 {totalVol.toFixed(2)} m³</div>}
                    </div>
                  );
                })()}
                </>}
              </div>

              {/* ═══ CENTRAS: Brėžinys ═══ */}
              <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',position:'relative'}}>
                {/* Toolbar */}
                <div style={{background:'#fff',borderBottom:'1px solid #e5e7eb',padding:'5px 10px',display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  {/* Zoom */}
                  <button onClick={()=>zoomBy(1.25)} style={{background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:5,width:24,height:24,cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
                  <span style={{fontSize:11,fontWeight:600,color:'#383853',minWidth:38,textAlign:'center'}}>{Math.round(zoom*100)}%</span>
                  <button onClick={()=>zoomBy(1/1.25)} style={{background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:5,width:24,height:24,cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>−</button>
                  <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}} style={{background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:5,padding:'2px 7px',cursor:'pointer',fontSize:10,fontWeight:600,color:'#383853'}}>⟲</button>
                  <div style={{width:1,height:16,background:'#e5e7eb',margin:'0 3px'}}/>
                  {/* Režimų mygtukai */}
                  <button onClick={()=>{setMode('select');lineStartRef.current=null;setDrawingPoints([]);}}
                    style={{padding:'3px 10px',fontSize:11,fontWeight:700,borderRadius:5,border:'1px solid '+(mode==='select'?'#383853':'#e5e7eb'),background:mode==='select'?'#383853':'#f3f4f6',color:mode==='select'?'#fff':'#374151',cursor:'pointer'}}>
                    ↖ Pasirinkti
                  </button>
                  <button onClick={()=>{setMode('calibrate');setCalibPoints([]);lineStartRef.current=null;setDrawingPoints([]);}}
                    style={{padding:'3px 10px',fontSize:11,fontWeight:700,borderRadius:5,border:'1px solid '+(mode==='calibrate'?'#f59e0b':'#e5e7eb'),background:mode==='calibrate'?'#fef3c7':'#f3f4f6',color:mode==='calibrate'?'#92400e':'#374151',cursor:'pointer'}}>
                    📏 Kalibruoti
                  </button>
                  {/* Sluoksniai dropdown mygtukas */}
                  <div style={{position:'relative'}}>
                    <button onClick={()=>setShowLayersDropdown(v=>!v)} disabled={!selectedDrawing}
                      style={{padding:'3px 10px',fontSize:11,fontWeight:700,borderRadius:5,
                        border:'1px solid '+(showLayersDropdown?'#383853':'#e5e7eb'),
                        background:showLayersDropdown?'#383853':'#f3f4f6',
                        color:showLayersDropdown?'#fff':(activeLayer?activeLayer.color:'#374151'),
                        cursor:selectedDrawing?'pointer':'not-allowed',opacity:selectedDrawing?1:0.5,
                        display:'flex',alignItems:'center',gap:5}}>
                      {activeLayer && <div style={{width:8,height:8,borderRadius:'50%',background:activeLayer.color,flexShrink:0}}/>}
                      🗂 Sluoksniai{drawingLayers.length>0?` (${drawingLayers.length})`:''}
                      <span style={{fontSize:9,marginLeft:2}}>{showLayersDropdown?'▲':'▼'}</span>
                    </button>
                    {showLayersDropdown && selectedDrawing && (
                      <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:200,
                        background:'#fff',border:'1px solid #e5e7eb',borderRadius:10,
                        boxShadow:'0 8px 32px rgba(0,0,0,0.15)',width:260,padding:'8px'}}
                        onClick={e=>e.stopPropagation()}>
                        {/* Nauji sluoksniai */}
                        <div style={{fontSize:10,fontWeight:700,color:'#9ca3af',letterSpacing:0.5,marginBottom:6}}>PRIDĖTI SLUOKSNĮ</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8}}>
                          {SYSTEM_PRESETS.map(s => (
                            <button key={s.key} onClick={()=>addLayer(s.key)}
                              style={{padding:'3px 9px',fontSize:10,fontWeight:700,borderRadius:4,
                                border:'1.5px solid #383853',background:'#383853',color:'#fff',cursor:'pointer'}}>
                              + {s.label}
                            </button>
                          ))}
                        </div>
                        {/* Esami sluoksniai */}
                        {drawingLayers.length > 0 && <>
                          <div style={{height:1,background:'#f0f0f0',margin:'6px 0'}}/>
                          <div style={{fontSize:10,fontWeight:700,color:'#9ca3af',letterSpacing:0.5,marginBottom:6}}>ESAMI SLUOKSNIAI</div>
                          {drawingLayers.map(layer => {
                            const isActive = layer.id === activeLayerId;
                            const isVis = isLayerVisible(layer.id);
                            const mCount = markings.filter(m => m.layerId === layer.id).length;
                            return (
                              <div key={layer.id}
                                style={{display:'flex',alignItems:'center',gap:6,padding:'5px 7px',borderRadius:6,marginBottom:2,cursor:'pointer',
                                  background:isActive?layer.color+'18':'#f8f9fb',
                                  border:'1.5px solid '+(isActive?layer.color:'transparent')}}
                                onClick={()=>setActiveLayerId(isActive?null:layer.id)}>
                                <div style={{width:10,height:10,borderRadius:'50%',background:layer.color,flexShrink:0}}/>
                                <span style={{flex:1,fontSize:11,fontWeight:isActive?800:500,color:isActive?layer.color:'#1c1c1c'}}>
                                  {layer.label}
                                </span>
                                <span style={{fontSize:10,color:'#9ca3af'}}>{mCount}</span>
                                <button onClick={e=>{e.stopPropagation();setVisibleLayers(p=>({...p,[layer.id]:!isVis}));}}
                                  style={{background:'none',border:'none',cursor:'pointer',fontSize:12,padding:0,opacity:isVis?1:0.35,lineHeight:1}}>
                                  {isVis?'👁':'🙈'}
                                </button>
                                <button onClick={e=>{e.stopPropagation();
                                  if(markings.filter(m=>m.layerId===layer.id).length>0){
                                    if(!window.confirm(`Sluoksnyje "${layer.label}" yra ${markings.filter(m=>m.layerId===layer.id).length} žymėjimų. Ištrinti?`)) return;
                                    setMarkings(prev=>prev.filter(m=>m.layerId!==layer.id));
                                  }
                                  setLayers(prev=>prev.filter(l=>l.id!==layer.id));
                                  if(activeLayerId===layer.id) setActiveLayerId(null);
                                }} style={{background:'none',border:'none',cursor:'pointer',fontSize:14,color:'#d1d5db',padding:0,lineHeight:1}}>×</button>
                              </div>
                            );
                          })}
                          {activeLayer && (
                            <div style={{marginTop:6,padding:'4px 7px',background:activeLayer.color+'15',borderRadius:6,fontSize:10,fontWeight:700,color:activeLayer.color}}>
                              ✏ Žymima: {activeLayer.label}
                            </div>
                          )}
                        </>}
                        {drawingLayers.length === 0 && (
                          <div style={{fontSize:11,color:'#9ca3af',textAlign:'center',padding:'8px 0'}}>Nėra sluoksnių. Pridėkite naują.</div>
                        )}
                      </div>
                    )}
                  </div>
                  {calibration && (
                    <span style={{fontSize:10,color:'#059669',fontWeight:600,background:'#d1fae5',borderRadius:4,padding:'2px 6px',display:'flex',alignItems:'center',gap:4}}>
                      1px = {(calibration.realLength/calibration.pixelLength).toFixed(5)}m
                      <button onClick={()=>setCalibration(null)} title="Pašalinti kalibravimą"
                        style={{background:'none',border:'none',color:'#059669',cursor:'pointer',fontSize:12,padding:0,lineHeight:1}}>×</button>
                    </span>
                  )}
                  {!calibration && selectedDrawing && (
                    <span style={{fontSize:10,color:'#92400e',fontWeight:600,background:'#fef3c7',borderRadius:4,padding:'2px 6px'}}>
                      ⚠ Nekalibruota
                    </span>
                  )}
                  {mode==='draw' && activeTemplate && (
                    <>
                      <div style={{width:1,height:16,background:'#e5e7eb',margin:'0 3px'}}/>
                      <span style={{fontSize:11,fontWeight:700,color:'#735DFF',background:'#EEECFF',borderRadius:5,padding:'3px 8px'}}>
                        ✏ {activeTemplate.name}
                        {activeTemplate.shape==='line' && (
                          <span style={{fontWeight:400}}>
                            {drawingPoints.length===0 ? ' — spustelėkite pradžios tašką' : ' — spustelėkite pabaigos tašką · Enter = baigti'}
                          </span>
                        )}
                        {activeTemplate.shape==='polyline' && drawingPoints.length>0 && (
                          <span style={{fontWeight:400}}> — 2× spustelėjimas arba Enter = baigti</span>
                        )}
                      </span>
                      <button onClick={cancelDrawing} style={{background:'none',border:'1px solid #e5e7eb',borderRadius:5,padding:'2px 7px',fontSize:10,cursor:'pointer',color:'#6b7280'}}>✕ Atšaukti</button>
                    </>
                  )}
                  <div style={{marginLeft:'auto',display:'flex',gap:6,alignItems:'center'}}>
                    <button onClick={()=>setShowSummary(p=>!p)}
                      style={{padding:'3px 10px',fontSize:11,fontWeight:700,borderRadius:5,border:'1px solid '+(showSummary?'#383853':'#e5e7eb'),background:showSummary?'#383853':'#f3f4f6',color:showSummary?'#fff':'#374151',cursor:'pointer'}}>
                      📊 Suvestinė ({summary.length})
                    </button>
                    <button onClick={()=>{ setMultiExportDrawings(selectedDrawingId?[selectedDrawingId]:[]); const now=new Date(); setExportFileName('UAB_AMTEKA_ISPILDOMOSIOS_BENDRA_SUVESTINE_'+now.getFullYear()+'.'+(String(now.getMonth()+1).padStart(2,'0'))); setShowMultiExport(true); }}
                      style={{padding:'3px 10px',fontSize:11,fontWeight:700,borderRadius:5,border:'1px solid #383853',background:'#383853',color:'#fff',cursor:'pointer'}}>
                      📋 Bendra suvestinė
                    </button>
                    <button onClick={()=>setShowExport(true)} disabled={!selectedDrawing}
                      style={{padding:'3px 10px',fontSize:11,fontWeight:700,borderRadius:5,border:'1px solid #735DFF',background:'#735DFF',color:'#fff',cursor:selectedDrawing?'pointer':'not-allowed',opacity:selectedDrawing?1:0.5}}>
                      🖨 PDF Žiniaraštis
                    </button>
                  </div>
                </div>

                {/* ─── Sistemų sluoksniai — dropdown ─── */}
                {/* Grindinio zonos piešimo statusas */}
                {floorZoneMode && (
                  <div style={{background:'#fef3c7',borderBottom:'1px solid #fde68a',padding:'4px 12px',display:'flex',alignItems:'center',gap:8,fontSize:11,color:'#92400e',fontWeight:600,flexShrink:0}}>
                    <span>🌡 Piešiama grindinio šildymo zona: {floorZonePoints.length} taškai</span>
                    {floorZonePoints.length >= 3 && <span style={{fontWeight:400}}>— Enter: užfiksuoti · Esc: atšaukti</span>}
                    <button onClick={()=>{setFloorZonePoints([]);setFloorZoneMode(false);setFloorZoneLayerId(null);}}
                      style={{marginLeft:'auto',background:'none',border:'1px solid #fde68a',borderRadius:4,padding:'1px 8px',fontSize:10,cursor:'pointer',color:'#92400e'}}>✕ Atšaukti</button>
                  </div>
                )}

                {/* Canvas zona */}
                <div ref={containerRef}
                  style={{flex:1,overflow:'hidden',position:'relative',
                    cursor: roomMode || floorZoneMode ? 'crosshair' : mode==='calibrate'||mode==='draw' ? 'crosshair' : isPanning ? 'grabbing' : 'grab'}}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onWheel={handleWheel}
                  onClick={e=>{handleCanvasClick(e); if(showLayersDropdown) setShowLayersDropdown(false);}}
                  onDoubleClick={handleCanvasDblClick}>
                  {selectedDrawing ? (() => {
                    const buf = idbPdfs[selectedDrawing.id];
                    const pages = pdfPages[selectedDrawing.id];
                    const psz = pageSize[selectedDrawing.id] || { w: 800, h: 1000 };
                    return (
                      <div style={{transform:`translate(${pan.x}px,${pan.y}px) scale(${zoom})`,transformOrigin:'0 0',
                        position:'absolute',top:0,left:0,userSelect:'none',willChange:'transform'}}>
                        {/* PDF puslapis + SVG sluoksnis — susieti poziciniu konteineriu */}
                        <div style={{position:'relative', width: psz.w, height: psz.h, flexShrink:0}}>
                          {pages && pages[0]
                            ? <TakeoffPdfPage page={pages[0]} width={psz.w} height={psz.h} renderScale={pdfRenderScale} />
                            : <div style={{width:psz.w,height:psz.h,background:'#f3f4f6',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'#9ca3af',fontSize:13,borderRadius:8,gap:10,padding:16,boxSizing:'border-box',textAlign:'center'}}>
                                {!buf
                                  ? '📐 Pasirinkite brėžinį'
                                  : pdfLoadErrors[selectedDrawing.id]
                                    ? <>
                                        <div>⚠ Nepavyko įkelti PDF</div>
                                        <div style={{fontSize:11,color:'#ef4444'}}>{pdfLoadErrors[selectedDrawing.id]}</div>
                                        <button onClick={()=>{
                                          setPdfLoadErrors(prev => { const n = { ...prev }; delete n[selectedDrawing.id]; return n; });
                                          setPdfRetryTick(t => t + 1);
                                        }} style={{background:'#383853',color:'#fff',border:'none',borderRadius:6,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                                          ↻ Bandyti dar kartą
                                        </button>
                                      </>
                                    : '⏳ Kraunamas PDF...'
                                }
                              </div>
                          }
                          {/* SVG sluoksnis — tiksliai ant brėžinio, nepriklausomai nuo canvas renderinimo */}
                          <svg style={{position:'absolute',top:0,left:0,width:psz.w,height:psz.h,overflow:'visible',pointerEvents:'none'}}>
                            <g style={{pointerEvents:'all'}}>
                              {/* Patalpos */}
                              {roomsVisible && drawingRooms.map(room => {
                                if (!room.points || room.points.length < 3) return null;
                                const pts = room.points.map(p=>`${p.x},${p.y}`).join(' ');
                                const cx = room.points.reduce((a,p)=>a+p.x,0)/room.points.length;
                                const cy = room.points.reduce((a,p)=>a+p.y,0)/room.points.length;
                                const scale = getScale();
                                const h = parseFloat(roomCeilingHeight)||0;
                                const areaM2 = room.areaM2 || (scale ? calcRoomArea(room.points, scale)?.m2 : null);
                                const vol = (areaM2 && h) ? areaM2 * h : null;
                                const isHighlighted = room.id === highlightedRoomId;
                                return (
                                  <g key={room.id}>
                                    {isHighlighted && (
                                      <polygon points={pts} fill="none" stroke="#FFD700" strokeWidth={7/zoom}
                                        vectorEffect="non-scaling-stroke" shapeRendering="geometricPrecision" opacity={0.55}/>
                                    )}
                                    <polygon points={pts} fill={room.color+(isHighlighted?'55':'33')}
                                      stroke={isHighlighted?'#FFD700':room.color} strokeWidth={(isHighlighted?4:2)/zoom}
                                      vectorEffect="non-scaling-stroke" shapeRendering="geometricPrecision"/>
                                    <text x={cx} y={cy-6/zoom} textAnchor="middle" fontSize={12/zoom} fontWeight="700" fill={room.color} style={{pointerEvents:'none',userSelect:'none'}}>
                                      {room.name}
                                    </text>
                                    {areaM2 && (
                                      <text x={cx} y={cy+8/zoom} textAnchor="middle" fontSize={9/zoom} fill={room.color} style={{pointerEvents:'none',userSelect:'none'}}>
                                        {areaM2.toFixed(2)} m²{vol ? `  |  ${vol.toFixed(2)} m³` : ''}
                                      </text>
                                    )}
                                  </g>
                                );
                              })}
                              {/* Grindinio šildymo zonos */}
                              {drawingFloorZones.map((zone, zoneIdx) => {
                                if (!floorZonesVisible) return null;
                                if (zone.layerId && !isLayerVisible(zone.layerId)) return null;
                                // Spalva skaičiuojama pagal poziciją sąraše, ne saugoma objekte —
                                // taip kiekviena zona visada gauna skirtingą spalvą, iškart
                                // atsinaujina ir esamoms zonoms, be jokios rizikos, kad dvi
                                // zonos "susikirs" į tą pačią spalvą dėl kūrimo metu užšaldytos reikšmės.
                                const zoneColor = FLOOR_ZONE_PALETTE[zoneIdx % FLOOR_ZONE_PALETTE.length];

                                // Apjungta zona — piešiame kiekvieną patalpą atskirai
                                if ((!zone.points || zone.points.length < 3) && zone.mergedFrom && zone.mergedFrom.length > 0) {
                                  const mergedRooms = rooms.filter(r => zone.mergedFrom.includes(r.id) && r.points && r.points.length >= 3);
                                  if (mergedRooms.length === 0) return null;
                                  // Centras — visų patalpos centrų vidurkis
                                  const allPts = mergedRooms.flatMap(r => r.points);
                                  const cx = allPts.reduce((a,p)=>a+p.x,0)/allPts.length;
                                  const cy = allPts.reduce((a,p)=>a+p.y,0)/allPts.length;
                                  return (
                                    <g key={zone.id}>
                                      {mergedRooms.map(r => (
                                        <polygon key={r.id}
                                          points={r.points.map(p=>`${p.x},${p.y}`).join(' ')}
                                          fill={zoneColor+'25'} stroke={zoneColor}
                                          strokeWidth={2/zoom} strokeDasharray={`${8/zoom},${4/zoom}`} vectorEffect="non-scaling-stroke" shapeRendering="geometricPrecision"/>
                                      ))}
                                      <text x={cx} y={cy-5/zoom} textAnchor="middle" fontSize={11/zoom} fontWeight="700" fill={zoneColor} style={{pointerEvents:'none',userSelect:'none'}}>
                                        {zone.label}
                                      </text>
                                      {zone.areaM2 && (() => {
                                        const h = parseFloat(roomCeilingHeight)||0;
                                        const vol = h ? zone.areaM2*h : null;
                                        return (
                                          <text x={cx} y={cy+7/zoom} textAnchor="middle" fontSize={9/zoom} fill={zoneColor} style={{pointerEvents:'none',userSelect:'none'}}>
                                            {zone.areaM2.toFixed(2)} m²{vol ? `  |  ${vol.toFixed(2)} m³` : ''}
                                          </text>
                                        );
                                      })()}
                                    </g>
                                  );
                                }

                                if (!zone.points || zone.points.length < 3) return null;
                                const pts = zone.points.map(p=>`${p.x},${p.y}`).join(' ');
                                const cx = zone.points.reduce((a,p)=>a+p.x,0)/zone.points.length;
                                const cy = zone.points.reduce((a,p)=>a+p.y,0)/zone.points.length;
                                return (
                                  <g key={zone.id}>
                                    <polygon points={pts} fill={zoneColor+'25'} stroke={zoneColor}
                                      strokeWidth={2/zoom} strokeDasharray={`${8/zoom},${4/zoom}`} vectorEffect="non-scaling-stroke" shapeRendering="geometricPrecision"/>
                                    <text x={cx} y={cy-5/zoom} textAnchor="middle" fontSize={11/zoom} fontWeight="700" fill={zoneColor} style={{pointerEvents:'none',userSelect:'none'}}>
                                      {zone.label}
                                    </text>
                                    {zone.areaM2 && (() => {
                                      const h = parseFloat(roomCeilingHeight)||0;
                                      const vol = h ? zone.areaM2*h : null;
                                      return (
                                        <text x={cx} y={cy+7/zoom} textAnchor="middle" fontSize={9/zoom} fill={zoneColor} style={{pointerEvents:'none',userSelect:'none'}}>
                                          {zone.areaM2.toFixed(2)} m²{vol ? `  |  ${vol.toFixed(2)} m³` : ''}
                                        </text>
                                      );
                                    })()}
                                  </g>
                                );
                              })}
                              {/* Grindinio zonos piešimo peržiūra */}
                              {floorZoneMode && floorZonePoints.length > 0 && (
                                <g>
                                  <polyline points={floorZonePoints.map(p=>`${p.x},${p.y}`).join(' ')}
                                    fill="none" stroke="#f59e0b" strokeWidth={2/zoom} vectorEffect="non-scaling-stroke"
                                    strokeDasharray={`${6/zoom},${3/zoom}`} shapeRendering="geometricPrecision"/>
                                  {floorZonePoints.map((p,i)=>(
                                    <circle key={i} cx={p.x} cy={p.y} r={4/zoom} fill="#f59e0b" stroke="#fff" strokeWidth={1.5/zoom} shapeRendering="geometricPrecision"/>
                                  ))}
                                </g>
                              )}

                              {/* Patalpos žymėjimo peržiūra — visada rodoma piešiant, nepriklausomai */}
                              {/* nuo "roomsVisible" (tai tik jau išsaugotų patalpų rodymo perjungiklis). */}
                              {roomMode && roomPoints.length > 0 && (() => {
                                const snapDist = ROOM_SNAP_PX / zoom;
                                const canClose = roomPoints.length >= 3;
                                const last = roomPoints[roomPoints.length - 1];
                                const nearStart = canClose && mousePos && Math.hypot(mousePos.x-roomPoints[0].x, mousePos.y-roomPoints[0].y) <= snapDist;
                                const snapPt = nearStart ? roomPoints[0] : (mousePos && roomPoints.find(p => Math.hypot(mousePos.x-p.x, mousePos.y-p.y) <= snapDist)) || null;
                                const previewEnd = snapPt || mousePos;
                                return (
                                  <g>
                                    <polyline points={roomPoints.map(p=>`${p.x},${p.y}`).join(' ')}
                                      fill="none" stroke="#f59e0b" strokeWidth={2/zoom} vectorEffect="non-scaling-stroke"
                                      strokeDasharray={`${6/zoom},${3/zoom}`} shapeRendering="geometricPrecision"/>
                                    {previewEnd && (
                                      <line x1={last.x} y1={last.y} x2={previewEnd.x} y2={previewEnd.y}
                                        stroke={nearStart ? '#16a34a' : '#f59e0b'} strokeWidth={2/zoom} vectorEffect="non-scaling-stroke"
                                        strokeDasharray={`${4/zoom},${3/zoom}`} shapeRendering="geometricPrecision" opacity={0.8}/>
                                    )}
                                    {roomPoints.map((p,i)=>{
                                      const isStart = i===0;
                                      const hovered = mousePos && Math.hypot(mousePos.x-p.x, mousePos.y-p.y) <= snapDist;
                                      const isClosePin = isStart && canClose;
                                      const r = (hovered ? 6.5 : (isClosePin ? 5 : 4)) / zoom;
                                      const fill = isClosePin ? (hovered ? '#16a34a' : '#22c55e') : '#f59e0b';
                                      return <circle key={i} cx={p.x} cy={p.y} r={r} fill={fill} stroke="#fff" strokeWidth={1.5/zoom} shapeRendering="geometricPrecision"/>;
                                    })}
                                  </g>
                                );
                              })()}
                              {/* Visi sužymėjimai */}
                              {drawingMarkings.map(m=>{
                                const tmpl=templates.find(t=>t.id===m.templateId);
                                return renderMarkingSvg(m,tmpl,m.id===selectedMarkingId);
                              })}
                              {/* Piešimo peržiūra */}
                              {renderPreviewSvg()}
                              {/* Kalibravimo taškai */}
                              {calibPoints.map((p,i)=>(
                                <circle key={i} cx={p.x} cy={p.y} r={5/zoom} fill='#f59e0b' stroke='#fff' strokeWidth={1.5/zoom}/>
                              ))}
                              {calibPoints.length===2 && (
                                <line x1={calibPoints[0].x} y1={calibPoints[0].y} x2={calibPoints[1].x} y2={calibPoints[1].y}
                                  stroke='#f59e0b' strokeWidth={2} vectorEffect="non-scaling-stroke"
                                  strokeDasharray={`${6/zoom},${3/zoom}`}/>
                              )}
                              {/* Piešimo taškai */}
                              {activeTemplate && activeTemplate.shape === 'line' && lineStartRef.current && (
                                <circle cx={lineStartRef.current.x} cy={lineStartRef.current.y} r={5/zoom} fill='#735DFF' stroke='#fff' strokeWidth={2/zoom}/>
                              )}
                              {activeTemplate && activeTemplate.shape === 'polyline' && drawingPoints.map((p,i)=>(
                                <circle key={i} cx={p.x} cy={p.y} r={4/zoom} fill='#735DFF' stroke='#fff' strokeWidth={1.5/zoom}/>
                              ))}
                            </g>
                          </svg>
                        </div>
                      </div>
                    );
                  })() : (
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#9ca3af'}}>
                      <div style={{fontSize:52,marginBottom:10}}>📐</div>
                      <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Įkelkite PDF brėžinį</div>
                      <button disabled={pdfDetecting} onClick={()=>fileInputRef.current&&fileInputRef.current.click()}
                        style={{background:'#383853',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',fontSize:13,fontWeight:700,cursor:pdfDetecting?'default':'pointer',opacity:pdfDetecting?0.6:1}}>
                        {pdfDetecting ? '⏳ Tikrinama...' : '+ Įkelti PDF'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ═══ DEŠINĖ: Suvestinė / info ═══ */}
              {showSummary && (
                <div style={{width:320,minWidth:320,background:'#fff',borderLeft:'1px solid #e5e7eb',display:'flex',flexDirection:'column',overflow:'hidden'}}>
                  <div style={{padding:'10px 12px 7px',borderBottom:'1px solid #f0f0f0',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <span style={{fontSize:11,fontWeight:800,color:'#1c1c1c',letterSpacing:0.5}}>📊 SUVESTINĖ</span>
                    <button onClick={()=>setShowSummary(false)} style={{background:'none',border:'none',fontSize:16,cursor:'pointer',color:'#9ca3af'}}>×</button>
                  </div>

                  {/* Kalibracijos juosta */}
                  <div style={{padding:'6px 12px',borderBottom:'1px solid #f0f0f0',background: scl ? '#f0fdf4' : '#fffbeb'}}>
                    {scl
                      ? <span style={{fontSize:10,color:'#15803d',fontWeight:600}}>✓ Kalibruota: 1 px = {scl.toFixed(6)} m</span>
                      : <span style={{fontSize:10,color:'#92400e',fontWeight:600}}>⚠ Nekalibruota — linijiniai kiekiai pikseliais (px)</span>
                    }
                  </div>

                  <div style={{flex:1,overflowY:'auto',padding:'8px 10px'}}>
                    {summary.length===0 && (
                      <div style={{fontSize:11,color:'#9ca3af',textAlign:'center',marginTop:24,lineHeight:1.6}}>
                        Nėra sužymėjimų.<br/>Pasirinkite šabloną ir žymėkite brėžinyje.
                      </div>
                    )}

                    {summary.map(({tmpl, items, count, isFloor}) => {
                      const isLinear = tmpl.shape === 'line' || tmpl.shape === 'polyline';
                      const posArr = Object.values(items);
                      return (
                        <div key={tmpl.id} style={{marginBottom:10,border:'1px solid #e5e7eb',borderRadius:8,overflow:'hidden'}}>
                          {/* Šablono antraštė */}
                          <div style={{display:'flex',alignItems:'center',gap:7,padding:'7px 10px',background:'#f8f9fb',borderBottom:'1px solid #e5e7eb'}}>
                            <span style={{fontSize:14,color:tmpl.color||'#735DFF',flexShrink:0,lineHeight:1}}>
                              {{line:'╱',polyline:'〜',circle:'●',rect:'■',triangle:'▲',triangle_down:'▼',diamond:'◆',star:'★',star6:'✶',cross:'✚',pentagon:'⬠',hexagon:'⬡',octagon:'⯃',arrow_right:'➤',arrow_left:'◄',arrow_both:'↔',circle_cross:'⊕',rect_cross:'⊞',double_circle:'◎',ellipse:'⬭'}[tmpl.shape]||'●'}
                            </span>
                            <span style={{fontSize:12,fontWeight:700,color:'#1c1c1c',flex:1}}>{tmpl.name}</span>
                            <span style={{fontSize:10,color:'#fff',fontWeight:700,background:'#383853',borderRadius:10,padding:'1px 7px',flexShrink:0}}>
                              {isFloor ? '🌡' : `${count} vnt.`}
                            </span>
                          </div>

                          {/* Žiniaraščio pozicijos */}
                          {posArr.length === 0 ? (
                            <div style={{padding:'6px 10px',fontSize:10,color:'#9ca3af',fontStyle:'italic'}}>Nėra priskirtų pozicijų</div>
                          ) : (
                            <div>
                              {/* Stulpelių antraštės */}
                              <div style={{display:'grid',gridTemplateColumns:'1fr 56px 56px',gap:4,padding:'4px 10px 2px',borderBottom:'1px solid #f3f4f6'}}>
                                <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:0.4}}>Pozicija</span>
                                <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:0.4,textAlign:'center'}}>Vnt.</span>
                                <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:0.4,textAlign:'right'}}>Kiekis</span>
                              </div>
                              {posArr.map(({pos, total}) => {
                                const needsCalib = isLinear && !scl;
                                const displayQty = needsCalib
                                  ? <span style={{color:'#f59e0b'}}>{total.toFixed(1)} <span style={{fontSize:9}}>px</span></span>
                                  : <span style={{color:'#1c1c1c'}}>{total % 1 === 0 ? total.toFixed(0) : total.toFixed(3)}</span>;
                                return (
                                  <div key={pos.itemId||pos.id}
                                    style={{display:'grid',gridTemplateColumns:'1fr 56px 56px',gap:4,padding:'5px 10px',borderBottom:'1px solid #f9fafb',alignItems:'center'}}>
                                    <div style={{minWidth:0}}>
                                      <div style={{fontSize:11,fontWeight:600,color:'#374151',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={pos.name}>
                                        {pos.name}
                                      </div>
                                      {pos.groupName && <div style={{fontSize:9,color:'#9ca3af',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{pos.groupName}</div>}
                                    </div>
                                    <div style={{textAlign:'center',fontSize:11,fontWeight:600,color:'#383853'}}>{pos.unit}</div>
                                    <div style={{textAlign:'right',fontSize:12,fontWeight:700}}>{displayQty}</div>
                                  </div>
                                );
                              })}
                              {/* Linijinių suma papildomai */}
                              {isLinear && (
                                <div style={{padding:'3px 10px 5px',display:'flex',justifyContent:'space-between',background:'#f8f9fb'}}>
                                  <span style={{fontSize:9,color:'#9ca3af'}}>
                                    {scl
                                      ? `Bendras ilgis: ${(markings.filter(m=>m.templateId===tmpl.id).reduce((s,m)=>s+lineLength(m.points),0)*scl).toFixed(3)} m`
                                      : `Bendras ilgis: ${markings.filter(m=>m.templateId===tmpl.id).reduce((s,m)=>s+lineLength(m.points),0).toFixed(1)} px`
                                    }
                                  </span>
                                  {!scl && <span style={{fontSize:9,color:'#f59e0b',fontWeight:600}}>Kalibruokite!</span>}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Bendra visų pozicijų suvestinė */}
                    {summary.length > 1 && (() => {
                      const allItems = {};
                      summary.forEach(({items}) => Object.values(items).forEach(({pos, total}) => {
                        const k = pos.itemId || pos.name;
                        if (!allItems[k]) allItems[k] = { pos, total: 0 };
                        allItems[k].total += total;
                      }));
                      const entries = Object.values(allItems);
                      if (entries.length === 0) return null;
                      return (
                        <div style={{marginBottom:10,border:'2px solid #383853',borderRadius:8,overflow:'hidden'}}>
                          <div style={{padding:'6px 10px',background:'#383853'}}>
                            <span style={{fontSize:11,fontWeight:800,color:'#fff'}}>∑ VISO (visos pozicijos)</span>
                          </div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 56px 56px',gap:4,padding:'4px 10px 2px',borderBottom:'1px solid #e5e7eb',background:'#f8f9fb'}}>
                            <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase'}}>Pozicija</span>
                            <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',textAlign:'center'}}>Vnt.</span>
                            <span style={{fontSize:9,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',textAlign:'right'}}>Kiekis</span>
                          </div>
                          {entries.map(({pos, total}) => (
                            <div key={pos.itemId||pos.name}
                              style={{display:'grid',gridTemplateColumns:'1fr 56px 56px',gap:4,padding:'5px 10px',borderBottom:'1px solid #f9fafb',alignItems:'center',background:'#fff'}}>
                              <div style={{fontSize:11,fontWeight:600,color:'#374151',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={pos.name}>{pos.name}</div>
                              <div style={{textAlign:'center',fontSize:11,fontWeight:600,color:'#383853'}}>{pos.unit}</div>
                              <div style={{textAlign:'right',fontSize:12,fontWeight:800,color:'#1c1c1c'}}>
                                {total % 1 === 0 ? total.toFixed(0) : total.toFixed(3)}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Apatiniai mygtukai */}
                  <div style={{padding:'10px 12px',borderTop:'1px solid #e5e7eb',display:'flex',flexDirection:'column',gap:6}}>
                    <button onClick={transferToAtlikti}
                      style={{width:'100%',background:'#383853',color:'#fff',border:'none',borderRadius:7,padding:'9px',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                      ✅ Perkelti į Atliktus darbus
                    </button>
                    {markings.length>0 && (
                      <button onClick={()=>{if(window.confirm('Ištrinti VISUS sužymėjimus?'))setMarkings([]);}}
                        style={{width:'100%',background:'none',border:'1px solid #fecaca',color:'#ef4444',borderRadius:7,padding:'6px',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                        🗑 Išvalyti sužymėjimus
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* PDF eksporto modalas */}
              {showExport && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300}}>
                  <div style={{background:'#fff',borderRadius:12,padding:24,width:380,maxWidth:'95vw',boxShadow:'0 20px 60px rgba(0,0,0,0.22)'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                      <span style={{fontSize:15,fontWeight:800}}>🖨 PDF Žiniaraštis</span>
                      <button onClick={()=>setShowExport(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9ca3af'}}>×</button>
                    </div>

                    {/* Failo pavadinimas */}
                    <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:4}}>Ataskaitos pavadinimas</label>
                    <input type="text" value={exportFileName} onChange={e => setExportFileName(e.target.value)} placeholder="pvz. Takeoff suvestine 2026-05" style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'7px 10px',fontSize:12,boxSizing:'border-box',marginBottom:14}} />
                    {/* Laikotarpio pasirinkimas */}
                    <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:8}}>Laikotarpis</label>
                    <div style={{display:'flex',gap:6,marginBottom:12}}>
                      {[['week','Savaitė'],['month','Mėnesis'],['custom','Pasirinkti']].map(([v,l])=>(
                        <button key={v} onClick={()=>{
                          setExportPeriod(v);
                          const today = new Date();
                          if (v==='week') {
                            const day = today.getDay(), diff = day===0?-6:1-day;
                            const mon = new Date(today); mon.setDate(today.getDate()+diff);
                            const sun = new Date(mon); sun.setDate(mon.getDate()+6);
                            setExportDateFrom(mon.toISOString().split('T')[0]);
                            setExportDateTo(sun.toISOString().split('T')[0]);
                          } else if (v==='month') {
                            const first = new Date(today.getFullYear(), today.getMonth(), 1);
                            const last = new Date(today.getFullYear(), today.getMonth()+1, 0);
                            setExportDateFrom(first.toISOString().split('T')[0]);
                            setExportDateTo(last.toISOString().split('T')[0]);
                          }
                        }}
                          style={{flex:1,padding:'6px 4px',fontSize:11,fontWeight:700,borderRadius:6,
                            border:'2px solid '+(exportPeriod===v?'#383853':'#e5e7eb'),
                            background:exportPeriod===v?'#383853':'#f9fafb',
                            color:exportPeriod===v?'#fff':'#374151',cursor:'pointer'}}>
                          {l}
                        </button>
                      ))}
                    </div>

                    <div style={{display:'flex',gap:10,marginBottom:16}}>
                      <div style={{flex:1}}>
                        <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:3}}>Nuo</label>
                        <input type="date" value={exportDateFrom}
                          onChange={e=>{setExportDateFrom(e.target.value);setExportPeriod('custom');}}
                          style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}}/>
                      </div>
                      <div style={{flex:1}}>
                        <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:3}}>Iki</label>
                        <input type="date" value={exportDateTo}
                          onChange={e=>{setExportDateTo(e.target.value);setExportPeriod('custom');}}
                          style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}}/>
                      </div>
                    </div>

                    {/* Informacija apie žymėjimų kiekį */}
                    {(() => {
                      const cnt = markings.filter(m=>
                        m.drawingId===selectedDrawing?.id &&
                        m.createdAt>=exportDateFrom && m.createdAt<=exportDateTo
                      ).length;
                      return (
                        <div style={{padding:'8px 12px',background: cnt>0?'#f0fdf4':'#fffbeb',border:'1px solid '+(cnt>0?'#bbf7d0':'#fde68a'),borderRadius:7,marginBottom:14,fontSize:11}}>
                          {cnt>0
                            ? <span style={{color:'#15803d',fontWeight:600}}>✓ Rasti {cnt} žymėjimai pasirinktu laikotarpiu</span>
                            : <span style={{color:'#92400e',fontWeight:600}}>⚠ Nėra žymėjimų pasirinktu laikotarpiu</span>
                          }
                        </div>
                      );
                    })()}

                    {/* PDF struktūra */}
                    <div style={{background:'#f8f9fb',border:'1px solid #e5e7eb',borderRadius:7,padding:'8px 12px',marginBottom:16,fontSize:11,color:'#374151'}}>
                      <div style={{fontWeight:700,marginBottom:4}}>PDF struktūra:</div>
                      <div>📄 <strong>1 lapas</strong> — Brėžinys A3 (kraštovaizdis) su žymėjimais ir legenda</div>
                      <div>📄 <strong>2+ lapai</strong> — Kiekių suvestinė (pozicija · mato vnt. · kiekis)</div>
                    </div>

                    <div style={{display:'flex',gap:8}}>
                      <button
                        onClick={()=>generatePdf(exportDateFrom, exportDateTo, exportFileName)}
                        disabled={exporting}
                        style={{flex:1,background:exporting?'#e5e7eb':'#735DFF',color:exporting?'#9ca3af':'#fff',
                          border:'none',borderRadius:7,padding:'10px',fontSize:13,fontWeight:700,cursor:exporting?'not-allowed':'pointer'}}>
                        {exporting ? '⏳ Generuojama...' : '⬇ Generuoti PDF'}
                      </button>
                      <button onClick={()=>setShowExport(false)}
                        style={{background:'#f3f4f6',border:'none',borderRadius:7,padding:'10px 16px',fontSize:13,cursor:'pointer'}}>
                        Atšaukti
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Bendra suvestinė modalas */}
              {showMultiExport && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300}}>
                  <div style={{background:'#fff',borderRadius:12,padding:24,width:480,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.22)'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                      <span style={{fontSize:15,fontWeight:800}}>📋 Bendra suvestinė (keli brėžiniai)</span>
                      <button onClick={()=>setShowMultiExport(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9ca3af'}}>×</button>
                    </div>

                    {/* Pavadinimas */}
                    <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:4}}>Ataskaitos pavadinimas</label>
                    <input type="text" value={exportFileName} onChange={e=>setExportFileName(e.target.value)}
                      placeholder="pvz. Bendra suvestine 2026-05"
                      style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'7px 10px',fontSize:12,boxSizing:'border-box',marginBottom:14}}/>

                    {/* Laikotarpis */}
                    <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:8}}>Laikotarpis</label>
                    <div style={{display:'flex',gap:6,marginBottom:12}}>
                      {[['week','Savaitė'],['month','Mėnesis'],['custom','Pasirinkti']].map(([v,l])=>(
                        <button key={v} onClick={()=>{
                          setExportPeriod(v);
                          const today=new Date();
                          if(v==='week'){const day=today.getDay(),diff=day===0?-6:1-day;const mon=new Date(today);mon.setDate(today.getDate()+diff);const sun=new Date(mon);sun.setDate(mon.getDate()+6);setExportDateFrom(mon.toISOString().split('T')[0]);setExportDateTo(sun.toISOString().split('T')[0]);}
                          else if(v==='month'){const first=new Date(today.getFullYear(),today.getMonth(),1);const last=new Date(today.getFullYear(),today.getMonth()+1,0);setExportDateFrom(first.toISOString().split('T')[0]);setExportDateTo(last.toISOString().split('T')[0]);}
                        }} style={{flex:1,padding:'6px 4px',fontSize:11,fontWeight:700,borderRadius:6,border:'2px solid '+(exportPeriod===v?'#383853':'#e5e7eb'),background:exportPeriod===v?'#383853':'#f9fafb',color:exportPeriod===v?'#fff':'#374151',cursor:'pointer'}}>
                          {l}
                        </button>
                      ))}
                    </div>
                    <div style={{display:'flex',gap:10,marginBottom:16}}>
                      <div style={{flex:1}}>
                        <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:3}}>Nuo</label>
                        <input type="date" value={exportDateFrom} onChange={e=>{setExportDateFrom(e.target.value);setExportPeriod('custom');}} style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}}/>
                      </div>
                      <div style={{flex:1}}>
                        <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:3}}>Iki</label>
                        <input type="date" value={exportDateTo} onChange={e=>{setExportDateTo(e.target.value);setExportPeriod('custom');}} style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}}/>
                      </div>
                    </div>

                    {/* Brėžinių pasirinkimas */}
                    <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:8}}>Brėžiniai ir jų pavadinimai lentelėje</label>
                    <div style={{display:'flex',gap:8,marginBottom:8}}>
                      <button onClick={()=>setMultiExportDrawings(drawings.map(d=>d.id))}
                        style={{fontSize:11,padding:'4px 10px',borderRadius:5,border:'1px solid #e5e7eb',background:'#f3f4f6',cursor:'pointer',fontWeight:600}}>
                        ✓ Visi
                      </button>
                      <button onClick={()=>setMultiExportDrawings([])}
                        style={{fontSize:11,padding:'4px 10px',borderRadius:5,border:'1px solid #e5e7eb',background:'#f3f4f6',cursor:'pointer',fontWeight:600}}>
                        ✗ Nė vieno
                      </button>
                    </div>
                    <div style={{border:'1px solid #e5e7eb',borderRadius:7,overflow:'hidden',marginBottom:14,maxHeight:260,overflowY:'auto'}}>
                      {drawings.map((d,i) => {
                        const checked = multiExportDrawings.includes(d.id);
                        const cnt = markings.filter(m=>m.drawingId===d.id&&m.createdAt>=exportDateFrom&&m.createdAt<=exportDateTo).length;
                        const label = drawingLabels[d.id] !== undefined ? drawingLabels[d.id] : '';
                        return (
                          <div key={d.id}
                            style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:checked?'#f0f9ff':'#fff',borderBottom:i<drawings.length-1?'1px solid #f0f0f0':'none'}}>
                            {/* Varnelė */}
                            <div onClick={()=>setMultiExportDrawings(prev=>checked?prev.filter(x=>x!==d.id):[...prev,d.id])}
                              style={{width:16,height:16,borderRadius:4,border:'2px solid '+(checked?'#383853':'#d1d5db'),background:checked?'#383853':'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,cursor:'pointer'}}>
                              {checked && <span style={{color:'#fff',fontSize:10,lineHeight:1}}>✓</span>}
                            </div>
                            {/* Originalus pavadinimas */}
                            <span style={{fontSize:11,color:'#6b7280',flexShrink:0,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={d.name}>{d.name}</span>
                            {/* Rodyklė */}
                            <span style={{fontSize:10,color:'#d1d5db',flexShrink:0}}>→</span>
                            {/* Custom label input */}
                            <input
                              type="text"
                              value={label}
                              onChange={e=>setDrawingLabels(prev=>({...prev,[d.id]:e.target.value}))}
                              placeholder={d.name}
                              style={{flex:1,border:'1px solid '+(checked?'#c7d2fe':'#e5e7eb'),borderRadius:4,padding:'3px 6px',fontSize:11,background:checked?'#fff':'#f9fafb',color:'#111',minWidth:0}}
                            />
                            {/* Žymėjimų skaičius */}
                            <span style={{fontSize:10,color:cnt>0?'#16a34a':'#9ca3af',fontWeight:600,flexShrink:0}}>{cnt} žym.</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Info */}
                    {(() => {
                      const totalCnt = markings.filter(m=>multiExportDrawings.includes(m.drawingId)&&m.createdAt>=exportDateFrom&&m.createdAt<=exportDateTo).length;
                      return (
                        <div style={{padding:'8px 12px',background:totalCnt>0?'#f0fdf4':'#fffbeb',border:'1px solid '+(totalCnt>0?'#bbf7d0':'#fde68a'),borderRadius:7,marginBottom:14,fontSize:11}}>
                          {multiExportDrawings.length===0
                            ? <span style={{color:'#92400e',fontWeight:600}}>⚠ Nepasirinktas nė vienas brėžinys</span>
                            : totalCnt>0
                              ? <span style={{color:'#15803d',fontWeight:600}}>✓ {multiExportDrawings.length} brėžin. · {totalCnt} žymėjimų</span>
                              : <span style={{color:'#92400e',fontWeight:600}}>⚠ Nėra žymėjimų pasirinktu laikotarpiu</span>
                          }
                        </div>
                      );
                    })()}

                    {/* PDF struktūra */}
                    <label style={{fontSize:11,fontWeight:700,color:'#374151',display:'block',marginBottom:8}}>PDF struktūra</label>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:14}}>
                      {[
                        ['grouped','Sugrupuota','Br.1 -> Br.2 -> Lent.1 -> Lent.2 -> Bendra'],
                        ['paired', 'Porine',    'Br.1 -> Lent.1 -> Br.2 -> Lent.2 -> Bendra'],
                      ].map(([val,label,desc])=>(
                        <div key={val} onClick={()=>setPdfStructure(val)}
                          style={{border:'2px solid '+(pdfStructure===val?'#383853':'#e5e7eb'),borderRadius:8,padding:'10px 12px',cursor:'pointer',background:pdfStructure===val?'#f0f4f4':'#fafafa',transition:'all .15s'}}>
                          <div style={{fontWeight:700,fontSize:12,color:pdfStructure===val?'#383853':'#374151',marginBottom:3}}>{label}</div>
                          <div style={{fontSize:10,color:'#6b7280',lineHeight:1.4}}>{desc}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{display:'flex',gap:8}}>
                      <button onClick={()=>generateMultiPdf(multiExportDrawings,exportDateFrom,exportDateTo,exportFileName,drawingLabels,pdfStructure)}
                        disabled={exporting||multiExportDrawings.length===0}
                        style={{flex:1,background:exporting||multiExportDrawings.length===0?'#e5e7eb':'#735DFF',color:exporting||multiExportDrawings.length===0?'#9ca3af':'#fff',border:'none',borderRadius:7,padding:'10px',fontSize:13,fontWeight:700,cursor:exporting||multiExportDrawings.length===0?'not-allowed':'pointer'}}>
                        {exporting?'⏳ Generuojama...':'⬇ Generuoti PDF'}
                      </button>
                      <button onClick={()=>setShowMultiExport(false)}
                        style={{background:'#f3f4f6',border:'none',borderRadius:7,padding:'10px 16px',fontSize:13,cursor:'pointer'}}>
                        Atšaukti
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Kalibravimo modalas */}
              {showCalibInput && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
                  <div style={{background:'#fff',borderRadius:12,padding:22,width:340,boxShadow:'0 16px 48px rgba(0,0,0,0.18)'}}>
                    <div style={{fontSize:14,fontWeight:800,marginBottom:10}}>📏 Atskaitos linijos ilgis</div>
                    <div style={{fontSize:12,color:'#6b7280',marginBottom:12}}>
                      Nupiešta linija pikseliais: <strong>{calibPoints.length===2 ? Math.round(Math.sqrt(Math.pow(calibPoints[1].x-calibPoints[0].x,2)+Math.pow(calibPoints[1].y-calibPoints[0].y,2))) : '—'} px</strong>
                    </div>
                    <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:4}}>Realus ilgis (metrais) *</label>
                    <input type="number" step="0.01" min="0.01" autoFocus
                      style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'7px 9px',fontSize:14,boxSizing:'border-box',marginBottom:12}}
                      value={calibRealLength} onChange={e=>setCalibRealLength(e.target.value)}
                      placeholder="pvz. 5.5"
                      onKeyDown={e=>{if(e.key==='Enter'&&calibRealLength){
                        const px=Math.sqrt(Math.pow(calibPoints[1].x-calibPoints[0].x,2)+Math.pow(calibPoints[1].y-calibPoints[0].y,2));
                        setCalibration({pixelLength:px,realLength:parseFloat(calibRealLength)});
                        setShowCalibInput(false);setCalibPoints([]);setCalibRealLength('');setMode('select');
                      }}}/>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={()=>{
                        if(!calibRealLength) return;
                        const px=Math.sqrt(Math.pow(calibPoints[1].x-calibPoints[0].x,2)+Math.pow(calibPoints[1].y-calibPoints[0].y,2));
                        setCalibration({pixelLength:px,realLength:parseFloat(calibRealLength)});
                        setShowCalibInput(false);setCalibPoints([]);setCalibRealLength('');setMode('select');
                      }} style={{flex:1,background:'#383853',color:'#fff',border:'none',borderRadius:7,padding:'8px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                        ✓ Nustatyti masteli
                      </button>
                      <button onClick={()=>{setShowCalibInput(false);setCalibPoints([]);setCalibRealLength('');}}
                        style={{background:'#f3f4f6',border:'none',borderRadius:7,padding:'8px 12px',fontSize:13,cursor:'pointer'}}>Atšaukti</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Kelių puslapių PDF skaidymo patvirtinimas */}
              {pdfPendingSplit && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
                  <div style={{background:'#fff',borderRadius:12,padding:22,width:380,boxShadow:'0 16px 48px rgba(0,0,0,0.18)'}}>
                    <div style={{fontSize:14,fontWeight:800,marginBottom:10}}>📄 Kelių puslapių PDF</div>
                    <div style={{fontSize:12,color:'#6b7280',marginBottom:16}}>
                      Faile <strong>{pdfPendingSplit.baseName}.pdf</strong> yra <strong>{pdfPendingSplit.numPages}</strong> puslapiai.
                      Ar norite kiekvieną puslapį įkelti kaip atskirą PDF brėžinį į projektą?
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={()=>{
                        const { file, buf, baseName, numPages } = pdfPendingSplit;
                        setPdfPendingSplit(null);
                        importPdfPages(file, buf, baseName, numPages);
                      }} style={{flex:1,background:'#383853',color:'#fff',border:'none',borderRadius:7,padding:'8px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                        ✓ Skaidyti į {pdfPendingSplit.numPages} brėžinius
                      </button>
                      <button onClick={()=>setPdfPendingSplit(null)}
                        style={{background:'#f3f4f6',border:'none',borderRadius:7,padding:'8px 12px',fontSize:13,cursor:'pointer'}}>Atšaukti</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Šablonų redaktorius modalas */}
              {showTemplateEditor && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
                  <TakeoffTemplateEditor
                    key={editingTemplate ? editingTemplate.id : '__new__'}
                    tmpl={editingTemplate}
                    allZinItems={allZinItems}
                    ziniarastis={ziniarastis}
                    existingGroups={templateGroups}
                    onNewGroup={ensureGroup}
                    onSave={t=>{
                      ensureGroup(t.groupName);
                      if(editingTemplate) setTemplates(ts=>ts.map(x=>x.id===t.id?t:x));
                      else setTemplates(ts=>[...ts,{...t,id:Date.now()}]);
                      setShowTemplateEditor(false);setEditingTemplate(null);
                    }}
                    onCancel={()=>{setShowTemplateEditor(false);setEditingTemplate(null);}}
                  />
                </div>
              )}

              {/* Konfigūratorius modalas */}
              {showKonfigurator && (
                <KonfiguratorModal
                  layers={drawingLayers}
                  rooms={drawingRooms}
                  markings={markings}
                  setMarkings={setMarkings}
                  templates={templates}
                  setTemplates={setTemplates}
                  templateGroups={templateGroups}
                  setTemplateGroups={setTemplateGroups}
                  calibration={calibration}
                  getScale={getScale}
                  selectedDrawingId={selectedDrawingId}
                  zoom={zoom}
                  floorZones={drawingFloorZones}
                  setFloorZones={setFloorZones}
                  floorZonesVisible={floorZonesVisible}
                  setFloorZonesVisible={setFloorZonesVisible}
                  roomCeilingHeight={roomCeilingHeight}
                  getLayerHeat={getLayerHeat}
                  setLayerHeat={setLayerHeat}
                  onStartFloorZone={(layerId) => {
                    setFloorZoneLayerId(layerId);
                    setFloorZoneMode(true);
                    setFloorZonePoints([]);
                    setShowKonfigurator(false);
                  }}
                  onOpenTemplateEditor={(tmplObj) => {
                    setShowKonfigurator(false);
                    // setTimeout leidžia setTemplates state atsinaujinti prieš atidarant kortelę
                    setTimeout(() => {
                      setEditingTemplate(tmplObj);
                      setShowTemplateEditor(true);
                    }, 50);
                  }}
                  onClose={()=>setShowKonfigurator(false)}
                />
              )}
            </div>
          );
        }
